import React, { useState, useEffect, useMemo, useRef } from 'react';
import { collection, doc, getDoc, getDocs, setDoc, serverTimestamp, query, where } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { getDailyUnits, getLanthandel, getBelaggning, budgetAssist, getPersonalSplit } from '../qlik';
import { raStartYear } from '../utils';
import { BOLAG, RADER, GRUPP_LABELS, VK_SCHABLON, raManader, MANAD_LABELS, summering, raVeckor, isoMandag } from './budgetModel';
import LikviditetsMatris from './LikviditetsMatris';
import UtfallsMatris from './UtfallsMatris';
import { autoTyp } from './KostnadsAnalys';
import { kontoGrupp } from '../sie';
import { utfallTillRader, slaIhop } from './utfallPrognos';

// ── BUDGET (endast admin) ─────────────────────────────────────────
// Budgetmatris per bolag × RÅ × version. Belopp lagras i kr (kostnader
// som positiva tal), redigeras i tkr. Generator bygger förslag från
// portalens utfall (Qlik daily_units / HonestBox) ELLER ett tidigare års
// budget × uppräknings-%.

const ACCENT = '#2D2D2D';
// Radstatus — diskret "var är jag"-markering i matrisen (cyklas med pricken)
const STATUS_FARG = { pagar: 'rgba(224,164,88,0.16)', klar: 'rgba(29,158,117,0.13)' };
const STATUS_PRICK = { pagar: '◐', klar: '●' };
const STATUS_TON = { pagar: '#C98A2E', klar: '#1D9E75' };
const kr0 = (v) => Math.round(v / 1000); // kr → tkr för visning
const btn = (active) => ({
  background: active ? ACCENT : 'transparent', color: active ? '#fff' : 'var(--text-secondary)',
  border: '1px solid var(--border)', borderRadius: 20, padding: '4px 12px',
  fontSize: 11.5, cursor: 'pointer', fontFamily: 'system-ui,sans-serif',
});
const actionBtn = {
  background: 'transparent', border: '1px solid var(--border)', borderRadius: 8,
  padding: '5px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'system-ui,sans-serif',
  color: 'var(--text)',
};

// Aktuellt + nästa RÅ som valbara budgetår ("2627" = jul 2026–jun 2027)
function raVal() {
  const y = raStartYear();
  const mk = (start) => `${String(start % 100).padStart(2, '0')}${String((start + 1) % 100).padStart(2, '0')}`;
  return [mk(y + 1), mk(y), mk(y - 1)]; // nästa, innevarande, förra
}
const raLabel2 = (ra) => `RÅ ${ra.slice(0, 2)}/${ra.slice(2)}`;

// ISO-veckonyckel (yyyyww) för ett ISO-datum
function veckoKeyAv(iso) {
  const t = new Date(iso + 'T12:00:00Z');
  const dow = (t.getUTCDay() + 6) % 7;
  t.setUTCDate(t.getUTCDate() - dow + 3);
  const y = t.getUTCFullYear();
  const jan4 = new Date(Date.UTC(y, 0, 4));
  const w = 1 + Math.round(((t - jan4) / 86400000 - 3 + ((jan4.getUTCDay() + 6) % 7)) / 7);
  return y * 100 + w;
}

// ── VECKOBUDGET — budgetera en intäktsrad per vecka, med stöd av
// fjolårets utfall samma vecka och (för Hotell Logi) det som redan
// ligger på böckerna. Förslag = max(bokat, ifjol × uppräkning).
// Skrivs upp till månadsmatrisen med 1/7 av veckobeloppet per dag.
function VeckoBudget({ ra, bolag, radDef, raderDef, rader, veckorAlla, varden, foljUpp, onToggleFoljUpp, onSetVecka, onApply, onClose }) {
  const [upp, setUpp] = useState(6.5);
  const [ifjol, setIfjol] = useState(null);   // veckoKey → kr (utfall ifjol, −100)
  const [bokat, setBokat] = useState(null);   // veckoKey → kr (logi på böckerna)
  const [dagData, setDagData] = useState({}); // datum → kr (för verklig dagsfördelning)
  const veckor = useMemo(() => raVeckor(ra), [ra]);
  const arLogi = radDef.qlikEnhet === 'Hotell Logi';
  // Assistent-chat (veckoläge)
  const [medd, setMedd] = useState([]);       // [{roll, text, veckor?}]
  const [input, setInput] = useState('');
  const [jobb, setJobb] = useState(false);
  const [fel, setFel] = useState('');
  const [valda, setValda] = useState({});     // veckoKey → bool (senaste förslaget)

  useEffect(() => {
    let live = true;
    (async () => {
      const perVecka = {};
      const perDag = {};
      if (bolag === 'stigmyhrs') {
        const lh = await getLanthandel();
        for (const [datum, oms] of (lh?.days || [])) {
          const k = veckoKeyAv(datum);
          perVecka[k] = (perVecka[k] || 0) + oms;
          perDag[datum] = (perDag[datum] || 0) + oms;
        }
      } else if (radDef.qlikEnhet) {
        const units = await getDailyUnits();
        for (const u of units) {
          if (u.enhet !== radDef.qlikEnhet) continue;
          const k = veckoKeyAv(u.datum);
          perVecka[k] = (perVecka[k] || 0) + (u.intakt || 0);
          perDag[u.datum] = (perDag[u.datum] || 0) + (u.intakt || 0);
        }
      }
      if (live) { setIfjol(perVecka); setDagData(perDag); }
      if (arLogi) {
        const bel = await getBelaggning();
        const perV = {};
        for (const [datum, rec] of Object.entries(bel.byDatum || {})) {
          const k = veckoKeyAv(datum);
          perV[k] = (perV[k] || 0) + (rec.logi || 0);
        }
        // Bokningsvärdet är inkl moms (gästpris) — budgeten är ex moms.
        // ÷1,12 (logimoms) så äpplen jämförs med äpplen i hela panelen.
        for (const k of Object.keys(perV)) perV[k] = Math.round(perV[k] / 1.12);
        if (live) setBokat(perV);
      } else if (live) setBokat({});
    })().catch(console.error);
    return () => { live = false; };
  }, [bolag, radDef, arLogi]);

  // Passerad vecka = söndagen har varit → verkligt utfall finns i dagsdatan
  const idagIso = new Date().toISOString().slice(0, 10);
  const arPasserad = (k) => {
    const son = new Date(isoMandag(k)); son.setUTCDate(son.getUTCDate() + 6);
    return son.toISOString().slice(0, 10) < idagIso;
  };
  const utfallAv = (k) => Math.round((ifjol || {})[k] || 0); // kartan täcker alla veckor, även innevarande år

  const forslag = (k) => {
    // Passerade veckor: verkligt utfall (exkl moms) — INTE bokningsvärdet,
    // som är brutto och kan ligga ~15–20 % över kassaförd intäkt
    if (arPasserad(k) && utfallAv(k) > 0) return utfallAv(k);
    const fj = (ifjol || {})[k - 100] || 0;
    const uppr = Math.round(fj * (1 + upp / 100));
    const bok = arLogi ? Math.round((bokat || {})[k] || 0) : 0;
    return Math.max(uppr, bok);
  };
  const fyllForslag = () => { for (const k of veckor) onSetVecka(k, forslag(k)); };
  const summa = veckor.reduce((s, k) => s + (varden[k] || 0), 0);

  // Vecka → månader: passerade veckor fördelas enligt VERKLIGT dagsutfall
  // (kalendermånaden träffas exakt, även i RÅ-/månadsskarvarna); framtida
  // veckor med 1/7 per dag som förut. Ersätter veckorTillManader här.
  const fordelaTillManader = () => {
    const perMan = {};
    for (const [key, kr] of Object.entries(varden)) {
      const belopp = kr || 0;
      if (!belopp) continue;
      const k = Number(key);
      const mon = isoMandag(k);
      const dagar = [];
      let tot = 0;
      for (let i = 0; i < 7; i++) {
        const d = new Date(mon); d.setUTCDate(mon.getUTCDate() + i);
        const iso = d.toISOString().slice(0, 10);
        const v = dagData[iso] || 0;
        dagar.push({ d, v });
        tot += v;
      }
      const verkligaVikter = arPasserad(k) && tot > 0;
      for (const { d, v } of dagar) {
        const ym = `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
        perMan[ym] = (perMan[ym] || 0) + belopp * (verkligaVikter ? v / tot : 1 / 7);
      }
    }
    for (const ym of Object.keys(perMan)) perMan[ym] = Math.round(perMan[ym]);
    return perMan;
  };

  // Avstämning mot matrisen: vad veckorna skulle skriva vs vad raden har nu.
  // Gör otakt synlig direkt (t.ex. månad ändrad för hand efter veckoskrivning).
  const manaderRa = raManader(ra);
  const perManNu = fordelaTillManader();
  const tillMatris = manaderRa.reduce((s, m) => s + (perManNu[m] || 0), 0);
  const iMatris = manaderRa.reduce((s, m) => s + (((rader || {})[radDef.id] || {})[m] || 0), 0);
  const urTakt = Math.abs(tillMatris - iMatris) > 1000; // >1 tkr

  // Skriv till matrisen — varna först om befintliga månadsvärden skrivs över
  const skrivTillManader = () => {
    const rad = (rader || {})[radDef.id] || {};
    const forlorar = [];
    for (const m of manaderRa) {
      const nu = Math.round(rad[m] || 0);
      const ny = Math.round(perManNu[m] || 0);
      if (nu > 0 && nu !== ny) forlorar.push({ m, nu, ny });
    }
    if (forlorar.length) {
      const lista = forlorar.slice(0, 6)
        .map((a) => `  ${MANAD_LABELS[manaderRa.indexOf(a.m)]}: ${tkr0(a.nu)} → ${a.ny ? `${tkr0(a.ny)} tkr` : 'tomt'}`)
        .join('\n');
      const fler = forlorar.length > 6 ? `\n  … och ${forlorar.length - 6} månader till` : '';
      if (!window.confirm(`Veckofördelningen skriver över radens befintliga månadsvärden:\n\n${lista}${fler}\n\nFortsätt?`)) return;
    }
    onApply(perManNu);
  };
  const laddar = ifjol === null || bokat === null;
  const tkr0 = (v) => Math.round(v / 1000);

  // ── Assistent-chat: veckoförslag via budgetAssist (granularitet vecka) ──
  const skicka = async (fardigText) => {
    const text = (fardigText || input).trim();
    if (!text || jobb || laddar) return;
    setFel(''); setJobb(true); setInput('');
    const nya = [...medd, { roll: 'user', text }];
    setMedd(nya);
    try {
      const veckoUnderlag = veckor.map((k) => ({
        vecka: k,
        nuvarande: Math.round(varden[k] || 0),
        ifjol: Math.round((ifjol || {})[k - 100] || 0),
        utfall: arPasserad(k) ? utfallAv(k) : 0, // facit för passerade veckor (exkl moms)
        bokat: arLogi ? Math.round((bokat || {})[k] || 0) : 0,
        ...((foljUpp || {})[k] ? { foljUpp: true } : {}),
      }));
      // Övriga intäktsrader (månadsvärden) + logiradens veckobudget som drivare
      const MNRv = [7, 8, 9, 10, 11, 12, 1, 2, 3, 4, 5, 6];
      const manaderRa = raManader(ra);
      const tillMnrV = (ymMap) => {
        const ut = {};
        MNRv.forEach((mnr, i) => { const v = (ymMap || {})[manaderRa[i]] || 0; if (v) ut[mnr] = Math.round(v); });
        return ut;
      };
      const andraRader = (raderDef || [])
        .filter((r) => r.grupp === 'intakt' && r.id !== radDef.id && Object.keys((rader || {})[r.id] || {}).length)
        .map((r) => ({ rad: r.label, manader: tillMnrV(rader[r.id]) }));
      const logiVeckorRaw = radDef.id !== 'intakt:hotell' ? ((veckorAlla || {})['intakt:hotell'] || {}) : {};
      const logiVeckor = {};
      for (const [k, v] of Object.entries(logiVeckorRaw)) if (v) logiVeckor[k] = Math.round(v);

      const res = await budgetAssist({
        mode: 'chat',
        granularitet: 'vecka',
        radTyp: 'intakt',
        radLabel: radDef.label,
        radEnhet: radDef.qlikEnhet || '',
        andraRader,
        logiVeckor,
        bolagNamn: BOLAG.find((b) => b.id === bolag)?.namn || bolag,
        ra,
        veckoUnderlag,
        meddelanden: nya.map((m) => ({ roll: m.roll, text: m.text })),
      });
      setMedd((prev) => [...prev, { roll: 'assistant', text: res.svar, veckor: res.veckor || null }]);
      if (res.veckor?.length) {
        const v = {};
        for (const r of res.veckor) v[r.vecka] = !r.kraverGranskning;
        setValda(v);
      }
    } catch (e) {
      setFel(e.message || 'Assistenten svarade inte');
    } finally { setJobb(false); }
  };

  const senasteForslag = [...medd].reverse().find((m) => m.veckor?.length)?.veckor || null;
  const skrivInForslag = () => {
    for (const r of (senasteForslag || [])) {
      if (valda[r.vecka]) onSetVecka(r.vecka, r.belopp);
    }
  };
  const antalValda = senasteForslag ? senasteForslag.filter((r) => valda[r.vecka]).length : 0;

  const inp = {
    width: 62, border: '1px solid var(--border)', borderRadius: 5, padding: '2px 5px',
    fontSize: 11.5, textAlign: 'right', fontFamily: 'system-ui,sans-serif', background: 'var(--card)', color: 'var(--text)',
  };
  const td = { fontSize: 11.5, padding: '2px 8px 2px 0', fontFamily: 'system-ui,sans-serif', textAlign: 'right', whiteSpace: 'nowrap' };

  return (
    <div className="card" style={{ border: '2px solid #44365C' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
        <div className="card-title" style={{ marginBottom: 0 }}>
          🗓 Veckobudget — {radDef.label} · {raLabel2(ra)}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontFamily: 'system-ui,sans-serif', fontSize: 12 }}>
          <span>Uppräkning mot ifjol %</span>
          <input type="number" step="0.5" value={upp} onChange={(e) => setUpp(Number(e.target.value))} style={{ ...inp, width: 52 }} />
          <button onClick={fyllForslag} disabled={laddar} style={{ background: '#44365C', color: '#fff', border: 'none', borderRadius: 8, padding: '5px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'system-ui,sans-serif' }}>
            ⚡ Fyll med förslag
          </button>
          <button onClick={onClose} style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: 8, padding: '5px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'system-ui,sans-serif', color: 'var(--text)' }}>Stäng</button>
        </div>
      </div>
      {laddar && <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Hämtar fjolårsutfall{arLogi ? ' och bokningsläge' : ''}…</div>}
      {!laddar && (
        <>
          <div style={{ maxHeight: 420, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr style={{ position: 'sticky', top: 0, background: 'var(--card)', zIndex: 1 }}>
                  <th style={{ ...td, textAlign: 'left', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary)', padding: '6px 8px' }}>Vecka</th>
                  <th style={{ ...td, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary)' }}>Ifjol utfall</th>
                  <th style={{ ...td, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary)' }}>Utfall i år</th>
                  {arLogi && <th style={{ ...td, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary)' }}>På böckerna (ex moms)</th>}
                  <th style={{ ...td, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary)' }}>Förslag</th>
                  <th style={{ ...td, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary)' }}>Budget (tkr)</th>
                  <th style={{ ...td, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary)', textAlign: 'center' }} title="Markera veckor du vill fördjupa dig i / följa upp">Följ upp</th>
                </tr>
              </thead>
              <tbody>
                {veckor.map((k) => {
                  const mon = isoMandag(k);
                  const fj = (ifjol || {})[k - 100] || 0;
                  const utf = arPasserad(k) ? utfallAv(k) : 0;
                  const bok = arLogi ? ((bokat || {})[k] || 0) : 0;
                  const fs = forslag(k);
                  const markerad = !!(foljUpp || {})[k];
                  return (
                    <tr key={k} style={{ borderTop: '1px solid var(--border)', background: markerad ? 'rgba(224,164,88,0.12)' : 'transparent' }}>
                      <td style={{ ...td, textAlign: 'left', padding: '3px 8px' }}>
                        <strong>V{k % 100}</strong>{markerad ? ' 📌' : ''} <span style={{ color: 'var(--text-secondary)', fontSize: 10.5 }}>{mon.getUTCDate()}/{mon.getUTCMonth() + 1} {String(Math.floor(k / 100)).slice(2)}</span>
                      </td>
                      <td style={{ ...td, color: 'var(--text-secondary)' }}>{fj > 0 ? `${tkr0(fj)} tkr` : '–'}</td>
                      <td style={{ ...td, fontWeight: utf > 0 ? 600 : 400, color: utf > 0 ? 'var(--text)' : 'var(--text-secondary)' }}>{utf > 0 ? `${tkr0(utf)} tkr` : '–'}</td>
                      {arLogi && <td style={{ ...td, color: bok > 0 ? '#1D9E75' : 'var(--text-secondary)', fontWeight: bok > 0 ? 600 : 400 }}>{bok > 0 ? `${tkr0(bok)} tkr` : '–'}</td>}
                      <td style={{ ...td, color: 'var(--text-secondary)' }}>{fs > 0 ? `${tkr0(fs)} tkr` : '–'}</td>
                      <td style={{ ...td }}>
                        <input type="number" style={inp} placeholder="0"
                          value={varden[k] ? tkr0(varden[k]) : ''}
                          onChange={(e) => onSetVecka(k, Math.round((Number(e.target.value) || 0) * 1000))} />
                      </td>
                      <td style={{ ...td, textAlign: 'center' }}>
                        <input type="checkbox" checked={markerad} onChange={() => onToggleFoljUpp(k)}
                          title="Markera för uppföljning/fördjupning" style={{ cursor: 'pointer' }} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, flexWrap: 'wrap', gap: 8, fontFamily: 'system-ui,sans-serif' }}>
            <span style={{ fontSize: 13, fontWeight: 700 }}>
              Summa året: {Math.round(summa / 1000).toLocaleString('sv-SE')} tkr
              {Object.keys(foljUpp || {}).length > 0 && (
                <span style={{ fontWeight: 400, fontSize: 11.5, color: 'var(--text-secondary)', marginLeft: 10 }}>
                  📌 Följ upp: {Object.keys(foljUpp).map(Number).sort((a, b) => a - b).map((k) => `v.${k % 100}`).join(', ')}
                </span>
              )}
            </span>
            <button onClick={skrivTillManader}
              style={{ background: '#44365C', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 16px', fontSize: 12.5, cursor: 'pointer', fontFamily: 'system-ui,sans-serif' }}>
              ✓ Skriv till månadsbudgeten
            </button>
          </div>

          {/* Avstämning vecka ↔ matris */}
          <div style={{ fontSize: 11.5, marginTop: 6, fontFamily: 'system-ui,sans-serif', color: urTakt ? '#A36B00' : 'var(--text-secondary)' }}>
            {urTakt
              ? `⚠ Ur takt: veckorna ger ${tkr0(tillMatris)} tkr till matrisen, raden har ${tkr0(iMatris)} tkr där nu (skillnad ${tkr0(Math.abs(tillMatris - iMatris))} tkr). "Skriv till månadsbudgeten" synkar — eller lämna som det är om månaderna är rättade för hand.`
              : `✓ I takt med matrisen (${tkr0(iMatris)} tkr på raden).`}
          </div>

          {/* ── Assistent (veckoläge): fråga/instruera, förslag per vecka ── */}
          <div style={{ marginTop: 14, borderTop: '1px dashed var(--border)', paddingTop: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif', marginBottom: 8 }}>✨ Assistent</div>
            {medd.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10, maxHeight: 320, overflowY: 'auto' }}>
                {medd.map((m, i) => (
                  <div key={i} style={{
                    alignSelf: m.roll === 'user' ? 'flex-end' : 'flex-start',
                    maxWidth: '85%', borderRadius: 10, padding: '7px 11px',
                    background: m.roll === 'user' ? '#44365C' : 'var(--surface-2)',
                    color: m.roll === 'user' ? '#fff' : 'var(--text)',
                    fontSize: 12.5, fontFamily: 'system-ui,sans-serif', whiteSpace: 'pre-wrap', lineHeight: 1.45,
                  }}>
                    {m.text}
                    {m.veckor?.length ? <div style={{ fontSize: 10.5, marginTop: 5, opacity: 0.7 }}>↳ förslag på {m.veckor.length} veck{m.veckor.length > 1 ? 'or' : 'a'} — se tabellen nedan</div> : null}
                  </div>
                ))}
                {jobb && <div style={{ alignSelf: 'flex-start', fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>🤔 Resonerar…</div>}
              </div>
            )}
            {medd.length === 0 && !jobb && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                <button onClick={() => skicka('Föreslå veckobudget för hela året. Resonera kring säsongerna och redogör kort för hur du tänker.')} disabled={laddar}
                  style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: 20, padding: '4px 12px', fontSize: 11.5, cursor: 'pointer', fontFamily: 'system-ui,sans-serif', color: 'var(--text)' }}>
                  🤔 Föreslå hela året
                </button>
                <button onClick={() => skicka('Vilka veckor sticker ut i underlaget — starkast, svagast, och var skiljer sig bokningsläget mest mot ifjol? Inga belopp än.')} disabled={laddar}
                  style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: 20, padding: '4px 12px', fontSize: 11.5, cursor: 'pointer', fontFamily: 'system-ui,sans-serif', color: 'var(--text)' }}>
                  💬 Vilka veckor sticker ut?
                </button>
              </div>
            )}
            <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
              <textarea
                value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); skicka(); } }}
                placeholder='T.ex. "sportlovsveckorna +10 % mot ifjol, påsken ligger v.13–14 i år" — assistenten ser ifjol, bokningsläge och nuvarande per vecka…'
                rows={2}
                style={{ flex: 1, border: '1px solid var(--border)', borderRadius: 8, padding: 8, fontSize: 12.5, fontFamily: 'system-ui,sans-serif', background: 'var(--card)', color: 'var(--text)', resize: 'vertical' }} />
              <button onClick={() => skicka()} disabled={jobb || !input.trim()}
                style={{ background: input.trim() && !jobb ? '#44365C' : 'transparent', color: input.trim() && !jobb ? '#fff' : 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 16px', fontSize: 12.5, cursor: 'pointer', fontFamily: 'system-ui,sans-serif' }}>
                Skicka
              </button>
            </div>
            {fel && <div style={{ fontSize: 12, color: '#A32D2D', fontFamily: 'system-ui,sans-serif', marginTop: 6 }}>{fel}</div>}

            {senasteForslag && (
              <>
                <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8, marginTop: 10 }}>
                  <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                    <thead>
                      <tr style={{ position: 'sticky', top: 0, background: 'var(--card)', zIndex: 1 }}>
                        {['', 'Vecka', 'Ifjol', ...(arLogi ? ['Bokat'] : []), 'Nuvarande', 'Förslag', 'Motivering'].map((h, i, arr) => (
                          <th key={i} style={{ ...td, textAlign: i < 2 || i === arr.length - 1 ? 'left' : 'right', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary)', padding: '6px 8px 6px 0' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {senasteForslag.map((r) => {
                        const fj = (ifjol || {})[r.vecka - 100] || 0;
                        const bok = arLogi ? ((bokat || {})[r.vecka] || 0) : 0;
                        const nu = varden[r.vecka] || 0;
                        return (
                          <tr key={r.vecka} style={{ borderTop: '1px solid var(--border)', opacity: valda[r.vecka] ? 1 : 0.5 }}>
                            <td style={{ ...td, textAlign: 'left', width: 26, paddingLeft: 8 }}>
                              <input type="checkbox" checked={!!valda[r.vecka]} onChange={(e) => setValda((v) => ({ ...v, [r.vecka]: e.target.checked }))} />
                            </td>
                            <td style={{ ...td, textAlign: 'left', fontWeight: 600 }}>V{r.vecka % 100} <span style={{ color: 'var(--text-secondary)', fontSize: 10.5 }}>{String(Math.floor(r.vecka / 100)).slice(2)}</span>{r.kraverGranskning ? ' ⚠' : ''}</td>
                            <td style={{ ...td, color: 'var(--text-secondary)' }}>{fj ? `${tkr0(fj)} tkr` : '–'}</td>
                            {arLogi && <td style={{ ...td, color: bok ? '#1D9E75' : 'var(--text-secondary)' }}>{bok ? `${tkr0(bok)} tkr` : '–'}</td>}
                            <td style={{ ...td, color: 'var(--text-secondary)' }}>{nu ? `${tkr0(nu)} tkr` : '–'}</td>
                            <td style={{ ...td, fontWeight: 700, color: r.belopp !== nu ? '#44365C' : 'inherit' }}>{tkr0(r.belopp)} tkr</td>
                            <td style={{ ...td, textAlign: 'left', color: 'var(--text-secondary)', whiteSpace: 'normal', maxWidth: 280 }}>{r.motivering}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8, alignItems: 'center', fontFamily: 'system-ui,sans-serif' }}>
                  <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
                    {antalValda} av {senasteForslag.length} veckor valda{senasteForslag.some((r) => r.kraverGranskning) ? ' · ⚠-veckor kräver aktivt val' : ''}
                  </span>
                  <button onClick={skrivInForslag} disabled={antalValda === 0}
                    style={{ background: antalValda ? '#44365C' : 'transparent', color: antalValda ? '#fff' : 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: '6px 16px', fontSize: 12.5, cursor: 'pointer', fontFamily: 'system-ui,sans-serif' }}>
                    ✓ Skriv in valda veckor
                  </button>
                </div>
              </>
            )}
          </div>
          <div className="note">
            Alla belopp ex moms. Ifjol = samma ISO-vecka föregående år. Utfall i år = kassaförd intäkt för veckor som passerat — förslaget använder ALLTID utfallet för dessa. {arLogi ? 'På böckerna = bokat logivärde omräknat till ex moms (÷1,12) — kan ändå avvika några procent från slutligt utfall (frukostomföring, avbokningar). Framtida veckor: förslag = det högsta av bokat och ifjol × uppräkning. ' : 'Framtida veckor: förslag = ifjol × uppräkning. '}
            Vid skrivning till månadsbudgeten fördelas passerade veckor enligt verkligt dagsutfall (kalendermånaden träffas exakt) och framtida veckor med 1/7 per dag; dagar utanför räkenskapsåret faller bort.
          </div>
        </>
      )}
    </div>
  );
}

// ── PERSONAL: FAST/SÄSONG FRÅN LEDNING → PERSONAL ─────────────────
// Hämtar lönekostnad per månad × avdelning × anställningsform (kategorierna du
// satt i Personal-fliken) och skriver Fast personal + Säsongspersonal.
//
// Källan väljs per månad: har arbetsmånaden redan passerat använder vi ÅRETS
// EGET utfall rakt av (ingen uppräkning — beloppet är ju känt), annars
// fjolårets motsvarande månad × uppräkning. Samma princip som veckobudgeten.
//
// Två kalibreringar, verifierade mot bokföringen (Hotell AB):
//  · Qliks lönemått = bruttolön × 1,3142, dvs lön + LAGSTADGAD
//    arbetsgivaravgift. Avstämt jan–maj 2026: kvot 1,313 mot schablonens
//    1,3142. Det som INTE ligger med är pension (74xx), Fora-arbetsskade-
//    försäkring och särskild löneskatt (i 75xx men utanför schablonen) samt
//    utbildning (76xx). Hela personalkostnaden mot löner × 1,3142 per RÅ:
//    2,8 % (22/23) · 3,9 % (23/24) · 4,0 % (24/25) · 4,5 % (25/26 rensat för
//    en klump på konto 7410 i feb 2026, 291 tkr; med den 7,2 %).
//    Default 4 % = normalnivån.
//  · Personalkollen bokför lönen på ARBETSDAGEN, bokföringen på
//    UTBETALNINGSMÅNADEN → en månads förskjutning. Utan den blir maj 488
//    mot bokförda 1 259 (aprils arbete betalas i maj).
const foregaendeYm = (ym) => {
  let y = Number(ym.slice(0, 4)), m = Number(ym.slice(4)) - 1;
  if (m === 0) { m = 12; y -= 1; }
  return `${y}${String(m).padStart(2, '0')}`;
};
const ymLabel = (ym) => `${MANAD_LABELS[(Number(ym.slice(4)) + 5) % 12]} ${ym.slice(2, 4)}`;

function PersonalSplitPanel({ ra, bolag, onApply, onClose }) {
  const fjolRa = String(Number(ra.slice(0, 2)) - 1).padStart(2, '0') + ra.slice(0, 2);
  const [kallaRa, setKallaRa] = useState(fjolRa);
  const [upp, setUpp] = useState(3);
  const [paslag, setPaslag] = useState(4);
  const [forskjut, setForskjut] = useState(true);
  const [okatTill, setOkatTill] = useState('proportionellt');
  const [data, setData] = useState(null);
  const [arData, setArData] = useState(null);
  const [valda, setValda] = useState(null);
  const [jobb, setJobb] = useState(false);
  const [fel, setFel] = useState('');

  const manader = useMemo(() => raManader(ra), [ra]);
  const kallaManader = useMemo(() => raManader(kallaRa), [kallaRa]);
  const idagYm = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}`;
  }, []);

  const hamtaSpann = (franYm, tillYm) => {
    const sistaDag = new Date(Number(tillYm.slice(0, 4)), Number(tillYm.slice(4)), 0).getDate();
    return getPersonalSplit({
      from: `${franYm.slice(0, 4)}-${franYm.slice(4)}-01`,
      to: `${tillYm.slice(0, 4)}-${tillYm.slice(4)}-${String(sistaDag).padStart(2, '0')}`,
    });
  };

  const hamta = async () => {
    setJobb(true); setFel(''); setData(null); setArData(null);
    try {
      const res = await hamtaSpann(kallaManader[0], kallaManader[11]);
      setData(res);
      // Årets eget utfall för de månader som redan är helt passerade — de
      // slår fjolåret som underlag och hämtas som en andra körning.
      const klara = manader.filter((m) => m < idagYm);
      let ar = null;
      if (klara.length) {
        try { ar = await hamtaSpann(klara[0], klara[klara.length - 1]); setArData(ar); } catch { /* bonus — fall tillbaka på fjolåret */ }
      }
      const alla = [...new Set([...res.summor, ...(ar ? ar.summor : [])].map((x) => x.enhet))];
      // Sportcenter hör till SC AB sedan dec 2025 — förvalt bort ur Hotell AB
      // O/H hålls utanför fast/säsong — den enheten har en egen budgetrad.
      // Tas den med här hamnar ledningslönerna i Fast personal SAMTIDIGT som
      // generatorn lägger dem på O/H-raden, och summan blir ~2 mkr för hög.
      setValda(new Set(bolag === 'sc' ? alla.filter((e) => e === 'Sportcenter')
        : bolag === 'hotell' ? alla.filter((e) => e !== 'Sportcenter' && e !== 'OH')
          : alla.filter((e) => e !== 'OH')));
    } catch (e) {
      setFel(e.message || 'Kunde inte hämta personaldata');
    } finally { setJobb(false); }
  };

  const allaSummor = useMemo(
    () => (data ? [...data.summor, ...(arData ? arData.summor : [])] : []), [data, arData]);

  const perManad = useMemo(() => {
    if (!data || !valda) return null;
    const rakna = (ym) => {
      let fast = 0, sasong = 0, okat = 0;
      for (const s of allaSummor) {
        if (s.ym !== ym || !valda.has(s.enhet)) continue;
        if (s.kategori === 'fast') fast += s.lon;
        else if (s.kategori === 'sasong') sasong += s.lon;
        else okat += s.lon;
      }
      return { fast, sasong, okat };
    };
    const finns = new Set(allaSummor.map((s) => s.ym));
    return manader.map((m, i) => {
      // Förskjutning: budgetmånadens KOSTNAD bärs av föregående månads ARBETE
      const arbete = forskjut ? foregaendeYm(m) : m;
      if (arbete < idagYm && finns.has(arbete)) return { ym: m, ...rakna(arbete), kalla: arbete, verkligt: true };
      const k = kallaManader[forskjut ? (i + 11) % 12 : i];
      return { ym: m, ...rakna(k), kalla: k, verkligt: false };
    });
  }, [data, allaSummor, valda, manader, kallaManader, forskjut, idagYm]);

  // Okategoriserade fördelas enligt valt läge, sedan uppräkning + påslag.
  // Passerade månader räknas INTE upp — utfallet är redan årets nivå.
  const berakna = (r) => {
    let f = r.fast, s = r.sasong;
    const o = r.okat;
    if (okatTill === 'fast') f += o;
    else if (okatTill === 'sasong') s += o;
    else if (okatTill === 'proportionellt') {
      const bas = r.fast + r.sasong;
      if (bas > 0) { f += o * r.fast / bas; s += o * r.sasong / bas; } else { f += o; }
    }
    const k = (r.verkligt ? 1 : 1 + upp / 100) * (1 + paslag / 100);
    return { fast: Math.round(f * k), sasong: Math.round(s * k) };
  };
  const berakat = perManad ? perManad.map(berakna) : null;
  const summa = (falt) => (berakat || []).reduce((a, b) => a + b[falt], 0);

  // O/H-enhetens lön hämtas separat och skrivs till sin egen rad, med samma
  // uppräkning och påslag. Då täcker panelen hela lönesumman utan att
  // ledningslönerna hamnar i både Fast personal och O/H.
  const ohManad = useMemo(() => {
    if (!data) return null;
    const k = (1 + upp / 100) * (1 + paslag / 100);
    const ut = {};
    manader.forEach((m, i) => {
      const kalla = perManad ? perManad[i].kalla : null;
      if (!kalla) return;
      const kr = data.summor.filter((s) => s.ym === kalla && s.enhet === 'OH')
        .reduce((a, s) => a + s.lon, 0);
      if (kr) ut[m] = Math.round(kr * k);
    });
    return ut;
  }, [data, perManad, manader, upp, paslag]);

  const skriv = () => {
    const fastRad = {}, sasongRad = {};
    berakat.forEach((v, i) => {
      if (v.fast) fastRad[manader[i]] = v.fast;
      if (v.sasong) sasongRad[manader[i]] = v.sasong;
    });
    onApply({ 'personal:fast': fastRad, 'personal:sasong': sasongRad, 'personal:oh': ohManad || {} });
  };

  const inp = { width: 56, border: '1px solid var(--border)', borderRadius: 5, padding: '2px 5px', fontSize: 11.5, textAlign: 'right', fontFamily: 'system-ui,sans-serif', background: 'var(--card)', color: 'var(--text)' };
  const td = { fontSize: 11.5, padding: '3px 8px 3px 0', fontFamily: 'system-ui,sans-serif', textAlign: 'right', whiteSpace: 'nowrap' };
  const th = { ...td, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary)' };
  const okatTot = (perManad || []).reduce((s, r) => s + r.okat, 0);
  const antalVerkliga = (perManad || []).filter((r) => r.verkligt).length;

  return (
    <div className="card" style={{ border: '2px solid #44365C' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
        <div className="card-title" style={{ marginBottom: 0 }}>👥 Personal — fast/säsong från Ledning → Personal</div>
        <button onClick={onClose} style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: 8, padding: '5px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'system-ui,sans-serif', color: 'var(--text)' }}>Stäng</button>
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', fontFamily: 'system-ui,sans-serif', fontSize: 12, marginBottom: 10 }}>
        <span>Utfall från RÅ</span>
        <select value={kallaRa} onChange={(e) => { setKallaRa(e.target.value); setData(null); }}
          style={{ ...inp, width: 'auto', textAlign: 'left' }}>
          {[fjolRa, String(Number(ra.slice(0, 2)) - 2).padStart(2, '0') + String(Number(ra.slice(0, 2)) - 1).padStart(2, '0')].map((r) => (
            <option key={r} value={r}>{r.slice(0, 2)}/{r.slice(2)}</option>
          ))}
        </select>
        <span>Uppräkning %</span>
        <input type="number" step="0.5" value={upp} onChange={(e) => setUpp(Number(e.target.value))} style={inp} />
        <span title="Det Qlik INTE räknar med: pension (74xx), Fora-arbetsskadeförsäkring och särskild löneskatt (i 75xx men utanför AGA-schablonen) samt utbildning (76xx). Qlik = bruttolön × 1,3142 (lagstadgad AGA). Utfall Hotell AB: 22/23 2,8 % · 23/24 3,9 % · 24/25 4,0 % · 25/26 4,5 % (7,2 % med februariklumpen på konto 7410).">Påslag %</span>
        <input type="number" step="0.5" value={paslag} onChange={(e) => setPaslag(Number(e.target.value))} style={inp} />
        <label style={{ display: 'inline-flex', gap: 5, alignItems: 'center', cursor: 'pointer' }}
          title="Personalkollen bokför lönen på arbetsdagen, bokföringen på utbetalningsmånaden">
          <input type="checkbox" checked={forskjut} onChange={(e) => setForskjut(e.target.checked)} />
          Förskjut 1 mån
        </label>
        <button onClick={hamta} disabled={jobb}
          style={{ background: '#44365C', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 12.5, cursor: 'pointer', fontFamily: 'system-ui,sans-serif' }}>
          {jobb ? 'Hämtar…' : '↻ Hämta utfall'}
        </button>
      </div>
      {fel && <div style={{ fontSize: 12, color: '#A32D2D', fontFamily: 'system-ui,sans-serif', marginBottom: 8 }}>{fel}</div>}
      {jobb && <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Läser ett helt år ur Qlik, plus årets passerade månader — det tar en stund…</div>}

      {perManad && (
        <>
          {/* Avdelningar */}
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Avdelningar:</span>
            {[...new Set(allaSummor.map((s) => s.enhet))].sort().map((e) => {
              const med = valda.has(e);
              return (
                <button key={e} onClick={() => setValda((p) => { const n = new Set(p); if (med) n.delete(e); else n.add(e); return n; })}
                  style={{ background: med ? '#44365C' : 'transparent', color: med ? '#fff' : 'var(--text-secondary)',
                    border: '1px solid var(--border)', borderRadius: 20, padding: '3px 10px', fontSize: 11,
                    cursor: 'pointer', fontFamily: 'system-ui,sans-serif', textDecoration: med ? 'none' : 'line-through' }}>
                  {e}
                </button>
              );
            })}
          </div>

          {okatTot > 0 && (
            <div style={{ fontSize: 12, fontFamily: 'system-ui,sans-serif', marginBottom: 10, padding: '8px 10px', background: 'var(--surface-2)', borderRadius: 8 }}>
              <strong style={{ color: '#A36B00' }}>{data.antalPersoner.okat} personer saknar anställningsform</strong> ({Math.round(okatTot / 1000).toLocaleString('sv-SE')} tkr).
              Sätt dem i Ledning → Personal för bästa träffsäkerhet — eller välj hur de ska hanteras här:
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 6 }}>
                {[['proportionellt', 'Fördela proportionellt'], ['fast', 'Räkna som fast'], ['sasong', 'Räkna som säsong'], ['utelamna', 'Utelämna']].map(([v, l]) => (
                  <button key={v} onClick={() => setOkatTill(v)}
                    style={{ background: okatTill === v ? '#44365C' : 'transparent', color: okatTill === v ? '#fff' : 'var(--text-secondary)',
                      border: '1px solid var(--border)', borderRadius: 20, padding: '3px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'system-ui,sans-serif' }}>
                    {l}
                  </button>
                ))}
              </div>
              {data.okategoriserade?.length > 0 && (
                <div style={{ fontSize: 10.5, color: 'var(--text-secondary)', marginTop: 6 }}>
                  {data.okategoriserade.slice(0, 12).join(', ')}{data.okategoriserade.length > 12 ? ` … och ${data.okategoriserade.length - 12} till` : ''}
                </div>
              )}
            </div>
          )}

          {antalVerkliga > 0 && (
            <div style={{ fontSize: 12, fontFamily: 'system-ui,sans-serif', marginBottom: 10, padding: '8px 10px', background: 'linear-gradient(rgba(29,106,95,.10), rgba(29,106,95,.10)), var(--card)', borderRadius: 8 }}>
              <strong style={{ color: '#1D6A5F' }}>{antalVerkliga} {antalVerkliga === 1 ? 'månad bygger' : 'månader bygger'} på årets eget utfall</strong> — de räknas inte upp,
              beloppet är redan känt. Övriga {12 - antalVerkliga} kommer från {kallaRa.slice(0, 2)}/{kallaRa.slice(2)} × uppräkningen.
              {forskjut && ' Eftersom lönen betalas månaden efter arbetet flyttas brytpunkten en månad framåt i budgeten.'}
            </div>
          )}

          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead><tr>
              <th style={{ ...th, textAlign: 'left' }}>Månad</th>
              <th style={{ ...th, textAlign: 'left' }}>Källa{forskjut ? ' (arbete)' : ''}</th>
              <th style={th}>Fast utfall</th><th style={th}>Säsong utfall</th><th style={th}>Okat.</th>
              <th style={th}>→ Fast personal</th><th style={th}>→ Säsongspersonal</th>
            </tr></thead>
            <tbody>
              {perManad.map((r, i) => (
                <tr key={r.ym} style={{ borderTop: '1px solid var(--border)',
                  background: r.verkligt ? 'linear-gradient(rgba(29,106,95,.07), rgba(29,106,95,.07)), var(--card)' : undefined }}>
                  <td style={{ ...td, textAlign: 'left', fontWeight: 600 }}>{MANAD_LABELS[i]}</td>
                  <td style={{ ...td, textAlign: 'left', fontSize: 10.5, color: r.verkligt ? '#1D6A5F' : 'var(--text-secondary)', fontWeight: r.verkligt ? 700 : 400 }}>
                    {r.verkligt ? `utfall ${ymLabel(r.kalla)}` : `${ymLabel(r.kalla)} +${upp} %`}
                  </td>
                  <td style={{ ...td, color: 'var(--text-secondary)' }}>{Math.round(r.fast / 1000).toLocaleString('sv-SE')}</td>
                  <td style={{ ...td, color: 'var(--text-secondary)' }}>{Math.round(r.sasong / 1000).toLocaleString('sv-SE')}</td>
                  <td style={{ ...td, color: r.okat ? '#A36B00' : 'var(--text-secondary)' }}>{r.okat ? Math.round(r.okat / 1000).toLocaleString('sv-SE') : '–'}</td>
                  <td style={{ ...td, fontWeight: 700, color: '#1D6A5F' }}>{Math.round(berakat[i].fast / 1000).toLocaleString('sv-SE')}</td>
                  <td style={{ ...td, fontWeight: 700, color: '#C98A2E' }}>{Math.round(berakat[i].sasong / 1000).toLocaleString('sv-SE')}</td>
                </tr>
              ))}
              <tr style={{ borderTop: '2px solid var(--border)', fontWeight: 700 }}>
                <td style={{ ...td, textAlign: 'left' }}>SUMMA</td>
                <td />
                <td style={{ ...td, color: 'var(--text-secondary)' }}>{Math.round(perManad.reduce((s, r) => s + r.fast, 0) / 1000).toLocaleString('sv-SE')}</td>
                <td style={{ ...td, color: 'var(--text-secondary)' }}>{Math.round(perManad.reduce((s, r) => s + r.sasong, 0) / 1000).toLocaleString('sv-SE')}</td>
                <td style={{ ...td, color: 'var(--text-secondary)' }}>{okatTot ? Math.round(okatTot / 1000).toLocaleString('sv-SE') : '–'}</td>
                <td style={{ ...td, color: '#1D6A5F' }}>{Math.round(summa('fast') / 1000).toLocaleString('sv-SE')}</td>
                <td style={{ ...td, color: '#C98A2E' }}>{Math.round(summa('sasong') / 1000).toLocaleString('sv-SE')}</td>
              </tr>
            </tbody>
          </table>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10, alignItems: 'center', fontFamily: 'system-ui,sans-serif' }}>
            <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
              Totalt {Math.round((summa('fast') + summa('sasong')) / 1000).toLocaleString('sv-SE')} tkr
            </span>
            <button onClick={skriv}
              style={{ background: '#44365C', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 16px', fontSize: 12.5, cursor: 'pointer', fontFamily: 'system-ui,sans-serif' }}>
              ✓ Skriv till Fast + Säsong + O/H
            </button>
          </div>
        </>
      )}
      <div className="note">
        Alla belopp i tkr. <strong>O/H-enheten ligger utanför fast/säsong</strong> och skrivs till raden O/H — ledning &amp; adm.
        med samma uppräkning och påslag; annars skulle ledningslönerna hamna både i Fast personal och på O/H-raden.
        Utfallet kommer ur Personalkollen via Qlik och delas på anställningsform enligt kategorierna i Ledning → Personal
        (styrfilens fasta löner räknas alltid som fast). <strong>Månader vars arbetsmånad redan passerat hämtas ur årets eget utfall</strong> och
        räknas inte upp — bara framåtblickande månader bygger på fjolåret × uppräkning. <strong>Qliks lönemått = bruttolön × 1,3142</strong>, dvs
        lön plus lagstadgad arbetsgivaravgift (avstämt jan–maj 2026: kvot 1,313). Påslaget täcker det som ligger utanför schablonen — pension,
        Fora-arbetsskadeförsäkring, särskild löneskatt och utbildning: 2,8 % · 3,9 % · 4,0 % för RÅ 22/23–24/25 och 4,5 % för 25/26
        (7,2 % om man räknar in en klumpsumma på konto 7410 i februari 2026, 291 tkr — höj om den är återkommande).
        Förskjutningen finns för att Personalkollen bokför lönen på arbetsdagen medan bokföringen tar den på utbetalningsmånaden.
      </div>
    </div>
  );
}

// ── BUDGETASSISTENT (alla rader) ──────────────────────────────────
// Chattpanel mot budgetAssist-funktionen (Claude). Fungerar på alla
// radtyper: intäkter, varukostnader, personal, övriga, avskrivningar,
// räntor. Assistenten redogör för hur den tänker och kan bifoga ett
// radförslag som visas som diff. Vk-rader med schablon har dessutom en
// snabbknapp som räknar schablonen helt i frontend.
// Modellen skriver aldrig direkt — diffen accepteras rad för rad.
function AssistentPanel({ ra, bolag, radId, radDef, raderDef, rader, onApply, onClose }) {
  const sch = VK_SCHABLON[radId];
  const grupp = radDef.grupp;
  const [medd, setMedd] = useState([]);   // [{roll:'user'|'assistant', text, rader?}]
  const [input, setInput] = useState('');
  const [valda, setValda] = useState({}); // manad(nr) → bool (för senaste förslaget)
  const [jobb, setJobb] = useState(false);
  const [fel, setFel] = useState('');
  const [hist, setHist] = useState(null); // fjolår per ym (för Ifjol-kolumnen)
  const underlagRef = useRef(null);
  const manader = useMemo(() => raManader(ra), [ra]);
  const MNR = [7, 8, 9, 10, 11, 12, 1, 2, 3, 4, 5, 6];
  const ymAv = (mnr) => manader[MNR.indexOf(mnr)];
  const tkr0 = (v) => Math.round(v / 1000);

  const nuvarande = rader[radId] || {};
  // Relaterade intäkter: vk-rad → sin intäktsrad; kostnadsrader utan egen
  // intäktskoppling → summan av bolagets intäktsrader; intäktsrad → ingen.
  const intakterYm = useMemo(() => {
    if (sch) return rader[sch.intaktRad] || {};
    if (grupp === 'intakt') return {};
    const ut = {};
    for (const r of raderDef.filter((r) => r.grupp === 'intakt')) {
      for (const [ym, v] of Object.entries(rader[r.id] || {})) ut[ym] = (ut[ym] || 0) + v;
    }
    return ut;
  }, [rader, raderDef, sch, grupp]);

  const tillMnr = (ymMap) => {
    const ut = {};
    MNR.forEach((mnr) => { const v = ymMap[ymAv(mnr)] || 0; if (v) ut[mnr] = Math.round(v); });
    return ut;
  };

  // Underlag (fjolårshistorik + ev. bokningsläge) hämtas en gång per panel
  const byggUnderlag = async () => {
    if (underlagRef.current) return underlagRef.current;
    const fjolRa = String(Number(ra.slice(0, 2)) - 1).padStart(2, '0') + ra.slice(0, 2);
    const fjolMan = new Set(raManader(fjolRa));
    let historik = [];

    if (bolag === 'stigmyhrs') {
      if ((grupp === 'intakt' || grupp === 'vk') && radId.endsWith(':lanthandel')) {
        const lh = await getLanthandel();
        const perYm = {};
        for (const [datum, oms] of (lh?.days || [])) {
          const ym = datum.slice(0, 4) + datum.slice(5, 7);
          if (!fjolMan.has(ym)) continue;
          perYm[ym] = (perYm[ym] || 0) + oms;
        }
        historik = Object.entries(perYm).sort((a, b) => a[0].localeCompare(b[0]))
          .map(([arManad, v]) => ({
            arManad, intakt: Math.round(v),
            ...(grupp === 'intakt' ? { belopp: Math.round(v) } : {}),
          }));
      } else if (radId === 'intakt:huvudbutik') {
        // Huvudbutiken = bokförd intäkt (SIE) minus Lanthandelns kassaoms
        const [lh, snap] = await Promise.all([
          getLanthandel().catch(() => null),
          getDocs(query(collection(db, 'intel_bokforing'), where('bolag', '==', 'stigmyhrs'))),
        ]);
        const lhYm = {};
        for (const [datum, oms] of (lh?.days || [])) {
          const ym = datum.slice(0, 4) + datum.slice(5, 7);
          lhYm[ym] = (lhYm[ym] || 0) + oms;
        }
        const perYm = [];
        snap.forEach((d2) => {
          const v = d2.data();
          if (v.typ === 'meta' || !v.summor || !fjolMan.has(v.ym)) return;
          const belopp = Math.max(Math.round((v.summor.intakt || 0) - (lhYm[v.ym] || 0)), 0);
          perYm.push({ arManad: v.ym, intakt: belopp, belopp });
        });
        historik = perYm.sort((a, b) => a.arManad.localeCompare(b.arManad));
      }
      // vk:huvudbutik → tom historik här; den generella bokförings-
      // fallbacken nedan ger bolagets totala varukostnad som underlag
    } else {
      const enheter = radDef.qlikEnhet
        ? [radDef.qlikEnhet]
        : (grupp === 'personal'
          ? raderDef.filter((r) => r.grupp === 'intakt' && r.qlikEnhet).map((r) => r.qlikEnhet)
          : []);
      if (enheter.length) {
        const units = await getDailyUnits();
        const set = new Set(enheter);
        const perYm = {};
        for (const u of units) {
          if (!set.has(u.enhet)) continue;
          const ym = u.datum.slice(0, 4) + u.datum.slice(5, 7);
          if (!fjolMan.has(ym)) continue;
          const m = perYm[ym] || (perYm[ym] = { intakt: 0, vk: 0, lon: 0 });
          m.intakt += u.intakt || 0;
          m.vk += Math.max(Math.abs(u.kostnad || 0) - Math.abs(u.lon || 0), 0);
          m.lon += Math.abs(u.lon || 0);
        }
        historik = Object.entries(perYm).sort((a, b) => a[0].localeCompare(b[0]))
          .map(([arManad, v]) => {
            const belopp = grupp === 'vk' ? v.vk : grupp === 'personal' ? v.lon : grupp === 'intakt' ? v.intakt : null;
            return {
              arManad, intakt: Math.round(v.intakt),
              ...(belopp !== null ? { belopp: Math.round(belopp) } : {}),
            };
          });
      }
    }

    // Fallback: bokföring (SIE-uppladdningen) när portalens dagsdata saknar
    // raden — övriga kostnader/avskrivningar/räntor har ingen Qlik-källa alls,
    // och bokföringen täcker även tiden före Qlik-starten (dec 2024).
    if (!historik.length) {
      try {
        const snap = await getDocs(query(collection(db, 'intel_bokforing'), where('bolag', '==', bolag)));
        const perYm = [];
        snap.forEach((d) => {
          const v = d.data();
          if (v.typ === 'meta' || !v.summor || !fjolMan.has(v.ym)) return;
          const belopp = v.summor[grupp];
          if (typeof belopp !== 'number') return;
          perYm.push({ arManad: v.ym, intakt: Math.round(v.summor.intakt || 0), belopp: Math.round(belopp) });
        });
        historik = perYm.sort((a, b) => a.arManad.localeCompare(b.arManad));
      } catch (e) { console.error('bokföring för assistent:', e); }
    }

    // Bokningsläge ("på böckerna") för budgetåret — bara logiintäktsraden.
    // ÷1,12: bokningsvärdet är inkl logimoms, budgeten är ex moms.
    let bokningslage = {};
    if (grupp === 'intakt' && radDef.qlikEnhet === 'Hotell Logi') {
      const bel = await getBelaggning();
      const budgetMan = new Set(manader);
      const perYm = {};
      for (const [datum, rec] of Object.entries(bel.byDatum || {})) {
        const ym = datum.slice(0, 4) + datum.slice(5, 7);
        if (!budgetMan.has(ym)) continue;
        perYm[ym] = (perYm[ym] || 0) + (rec.logi || 0);
      }
      MNR.forEach((mnr) => { const v = perYm[ymAv(mnr)]; if (v) bokningslage[mnr] = Math.round(v / 1.12); });
    }

    // Ifjol-kolumnen i diff-tabellen (ym → radens utfall)
    const histYm = {};
    for (const h of historik) if (typeof h.belopp === 'number') {
      // fjolårets ym → budgetårets ym (samma månadsnr, +1 år)
      const mnr = Number(h.arManad.slice(4, 6));
      histYm[ymAv(mnr)] = h.belopp;
    }
    setHist(histYm);

    underlagRef.current = { historik, bokningslage };
    return underlagRef.current;
  };

  const skicka = async (fardigText) => {
    const text = (fardigText || input).trim();
    if (!text || jobb) return;
    setFel(''); setJobb(true); setInput('');
    const nya = [...medd, { roll: 'user', text }];
    setMedd(nya);
    try {
      const u = await byggUnderlag();
      // Bolagets övriga intäktsrader — drivare/sanity (logibudgeten speglar beläggningen)
      const andraRader = raderDef
        .filter((r) => r.grupp === 'intakt' && r.id !== radId && Object.keys(rader[r.id] || {}).length)
        .map((r) => ({ rad: r.label, manader: tillMnr(rader[r.id] || {}) }));
      const res = await budgetAssist({
        mode: 'chat',
        radLabel: radDef.label,
        radEnhet: radDef.qlikEnhet || '',
        radTyp: grupp,
        avdelning: sch?.avdelning,
        andraRader,
        bolagNamn: BOLAG.find((b) => b.id === bolag)?.namn || bolag,
        ra,
        intakter: tillMnr(intakterYm),
        nuvarande: tillMnr(nuvarande),
        historik: u.historik,
        bokningslage: u.bokningslage,
        meddelanden: nya.map((m) => ({ roll: m.roll, text: m.text })),
      });
      setMedd((prev) => [...prev, { roll: 'assistant', text: res.svar, rader: res.rader || null }]);
      if (res.rader?.length) {
        const v = {};
        for (const r of res.rader) v[r.manad] = !r.kraverGranskning;
        setValda(v);
      }
    } catch (e) {
      setFel(e.message || 'Assistenten svarade inte');
    } finally { setJobb(false); }
  };

  // Schablon-snabbknapp (vk-rader): helt i frontend, inget API-anrop
  const raknaSchablon = () => {
    const radR = MNR.map((mnr) => {
      const intakt = intakterYm[ymAv(mnr)] || 0;
      return {
        manad: mnr, belopp: Math.round(intakt * sch.pct), procent: sch.pct,
        motivering: 'Schablon', konfidens: 'hog', kraverGranskning: false,
      };
    });
    setMedd((prev) => [...prev, { roll: 'assistant', text: `Schablon ${(sch.pct * 100).toFixed(0)} % av budgeterad intäkt, rakt av per månad.`, rader: radR }]);
    const v = {}; for (const r of radR) v[r.manad] = true;
    setValda(v);
  };

  // Senaste förslaget (visas som diff under chatten)
  const senasteForslag = [...medd].reverse().find((m) => m.rader?.length)?.rader || null;

  const skrivIn = () => {
    const perYm = {};
    for (const r of (senasteForslag || [])) {
      if (!valda[r.manad]) continue;
      perYm[ymAv(r.manad)] = r.belopp;
    }
    onApply(perYm);
  };
  const antalValda = senasteForslag ? senasteForslag.filter((r) => valda[r.manad]).length : 0;
  const visaPct = grupp !== 'intakt';

  const td = { fontSize: 11.5, padding: '3px 8px 3px 0', fontFamily: 'system-ui,sans-serif', textAlign: 'right', whiteSpace: 'nowrap' };
  const snabb = {
    background: 'transparent', border: '1px solid var(--border)', borderRadius: 20,
    padding: '4px 12px', fontSize: 11.5, cursor: 'pointer', fontFamily: 'system-ui,sans-serif',
    color: 'var(--text)',
  };

  return (
    <div className="card" style={{ border: '2px solid #44365C' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
        <div className="card-title" style={{ marginBottom: 0 }}>
          ✨ Budgetassistent — {radDef.label}{sch ? ` · schablon ${(sch.pct * 100).toFixed(0)} %` : ''}
        </div>
        <button onClick={onClose} style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: 8, padding: '5px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'system-ui,sans-serif', color: 'var(--text)' }}>Stäng</button>
      </div>

      {/* Chatthistorik */}
      {medd.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10, maxHeight: 380, overflowY: 'auto' }}>
          {medd.map((m, i) => (
            <div key={i} style={{
              alignSelf: m.roll === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: '85%', borderRadius: 10, padding: '7px 11px',
              background: m.roll === 'user' ? '#44365C' : 'var(--surface-2)',
              color: m.roll === 'user' ? '#fff' : 'var(--text)',
              fontSize: 12.5, fontFamily: 'system-ui,sans-serif', whiteSpace: 'pre-wrap', lineHeight: 1.45,
            }}>
              {m.text}
              {m.rader?.length ? <div style={{ fontSize: 10.5, marginTop: 5, opacity: 0.7 }}>↳ förslag på {m.rader.length} månad{m.rader.length > 1 ? 'er' : ''} — se tabellen nedan</div> : null}
            </div>
          ))}
          {jobb && <div style={{ alignSelf: 'flex-start', fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>🤔 Resonerar…</div>}
        </div>
      )}

      {/* Snabbval vid tom chatt */}
      {medd.length === 0 && !jobb && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
          {sch && <button onClick={raknaSchablon} style={snabb}>⚡ Räkna schablon ({(sch.pct * 100).toFixed(0)} %)</button>}
          <button onClick={() => skicka('Föreslå budget för hela raden, alla 12 månader. Redogör kort för hur du tänker.')} style={snabb} disabled={jobb}>
            🤔 Föreslå hela raden
          </button>
          <button onClick={() => skicka('Vad säger underlaget om den här raden? Sammanfatta fjolåret och det som sticker ut, utan att föreslå belopp än.')} style={snabb} disabled={jobb}>
            💬 Vad säger underlaget?
          </button>
        </div>
      )}

      {/* Inmatning */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', marginBottom: senasteForslag ? 10 : 0 }}>
        <textarea
          value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); skicka(); } }}
          placeholder={medd.length ? 'Följdfråga eller justering, t.ex. "höj november — Bruksvallsloppet + SVT"…' : 'Fråga eller instruera assistenten — den ser budgeterade intäkter, fjolårets utfall och nuvarande värden…'}
          rows={2}
          style={{ flex: 1, border: '1px solid var(--border)', borderRadius: 8, padding: 8, fontSize: 12.5, fontFamily: 'system-ui,sans-serif', background: 'var(--card)', color: 'var(--text)', resize: 'vertical' }} />
        <button onClick={() => skicka()} disabled={jobb || !input.trim()}
          style={{ background: input.trim() && !jobb ? '#44365C' : 'transparent', color: input.trim() && !jobb ? '#fff' : 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 16px', fontSize: 12.5, cursor: 'pointer', fontFamily: 'system-ui,sans-serif' }}>
          Skicka
        </button>
      </div>
      {fel && <div style={{ fontSize: 12, color: '#A32D2D', fontFamily: 'system-ui,sans-serif', margin: '6px 0' }}>{fel}</div>}

      {/* Diff-tabell för senaste förslaget */}
      {senasteForslag && (
        <>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                {['', 'Månad', 'Ifjol', 'Nuvarande', 'Förslag', ...(visaPct ? ['%'] : []), 'Motivering'].map((h, i, arr) => (
                  <th key={i} style={{ ...td, textAlign: i < 2 || i === arr.length - 1 ? 'left' : 'right', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {senasteForslag.map((r) => {
                const ym = ymAv(r.manad);
                const nu = nuvarande[ym] || 0;
                const fj = (hist || {})[ym];
                const diff = r.belopp !== nu;
                return (
                  <tr key={r.manad} style={{ borderTop: '1px solid var(--border)', opacity: valda[r.manad] ? 1 : 0.5 }}>
                    <td style={{ ...td, textAlign: 'left', width: 26 }}>
                      <input type="checkbox" checked={!!valda[r.manad]} onChange={(e) => setValda((v) => ({ ...v, [r.manad]: e.target.checked }))} />
                    </td>
                    <td style={{ ...td, textAlign: 'left', fontWeight: 600 }}>{MANAD_LABELS[MNR.indexOf(r.manad)]}{r.kraverGranskning ? ' ⚠' : ''}</td>
                    <td style={{ ...td, color: 'var(--text-secondary)' }}>{typeof fj === 'number' ? `${tkr0(fj)} tkr` : '–'}</td>
                    <td style={{ ...td, color: 'var(--text-secondary)' }}>{nu ? `${tkr0(nu)} tkr` : '–'}</td>
                    <td style={{ ...td, fontWeight: 700, color: diff ? '#44365C' : 'inherit' }}>{tkr0(r.belopp)} tkr</td>
                    {visaPct && <td style={td}>{r.procent ? `${Math.round(r.procent * 100)}%` : '–'}</td>}
                    <td style={{ ...td, textAlign: 'left', color: 'var(--text-secondary)', whiteSpace: 'normal', maxWidth: 320 }}>{r.motivering}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10, alignItems: 'center', fontFamily: 'system-ui,sans-serif' }}>
            <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
              {antalValda} av {senasteForslag.length} månader valda{senasteForslag.some((r) => r.kraverGranskning) ? ' · ⚠-rader kräver aktivt val' : ''}
            </span>
            <button onClick={skrivIn} disabled={antalValda === 0}
              style={{ background: antalValda ? '#44365C' : 'transparent', color: antalValda ? '#fff' : 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: '6px 16px', fontSize: 12.5, cursor: 'pointer', fontFamily: 'system-ui,sans-serif' }}>
              ✓ Skriv in valda månader
            </button>
          </div>
        </>
      )}
      <div className="note">
        Assistenten skriver aldrig själv — förslag accepteras rad för rad och sparas först med Spara-knappen.
        Underlag: budgeterade intäkter, nuvarande värden, fjolårets utfall per månad (portalens dagsdata, annars uppladdad bokföring){grupp === 'intakt' && radDef.qlikEnhet === 'Hotell Logi' ? ' och bokningsläget' : ''}. Avvikelser flaggas ⚠.
      </div>
    </div>
  );
}

// isAdmin styr de delar som rör underlag utanför själva matrisen: generatorn
// läser hela bokföringen, fast/säsong-panelen läser individlöner ur Qlik.
// Matrisen i sig är öppen för den som fått budget-blocket tilldelat.
export default function BudgetTab({ isAdmin = false }) {
  const [ra, setRa] = useState(raVal()[1]);          // default innevarande RÅ
  const [bolag, setBolag] = useState('hotell');
  const [versioner, setVersioner] = useState([]);     // dokument för bolag+ra
  const [version, setVersion] = useState('B0');
  const [rader, setRader] = useState({});             // radId → { ym: kr }
  const [veckor, setVeckor] = useState({});           // radId → { veckoKey: kr } (veckobudget-underlag)
  const [foljUpp, setFoljUpp] = useState({});         // radId → { veckoKey: true } (veckor att fördjupa/följa upp)
  const [radStatus, setRadStatus] = useState({});     // radId → 'pagar' | 'klar' (arbetsläge i matrisen)
  const [veckoRad, setVeckoRad] = useState(null);     // radId med öppen veckobudget-panel
  const [assistRad, setAssistRad] = useState(null);   // vk-radId med öppen budgetassistent
  const [personalPanel, setPersonalPanel] = useState(false); // fast/säsong-panelen
  const [status, setStatus] = useState('utkast');
  const [forutsattningar, setForutsattningar] = useState('');
  const [dirty, setDirty] = useState(false);
  const [sparar, setSparar] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [msg, setMsg] = useState('');
  const [ihop, setIhop] = useState({}); // grupp → true = hopfälld (dragspel → kort resultatrapport)
  const [vy, setVy] = useState('budget');   // budget | likviditet
  const [likvid, setLikvid] = useState({}); // betalregler, forskott, ingaendeBank, ingaendeSkulder
  const [utfallTom, setUtfallTom] = useState(''); // P-version: sista månad med bokfört utfall ('' = ren budget)
  const [fyllerUtfall, setFyllerUtfall] = useState(false);

  const manader = useMemo(() => raManader(ra), [ra]);
  const raderDef = RADER[bolag];
  const docId = `${bolag}_${ra}_${version}`;

  // Lista versioner för bolag+RÅ
  useEffect(() => {
    let live = true;
    (async () => {
      const q1 = query(collection(db, 'intel_budget'), where('bolag', '==', bolag), where('ra', '==', ra));
      const snap = await getDocs(q1);
      // B0 först, sedan P-versionerna i nummerordning (P10 efter P9, inte efter P1)
      const nr = (v) => (v === 'B0' ? -1 : Number(String(v).replace(/^\D+/, '')) || 0);
      const vs = snap.docs.map((d) => ({ version: d.data().version, status: d.data().status }))
        .sort((a, b) => nr(a.version) - nr(b.version) || a.version.localeCompare(b.version));
      if (!live) return;
      setVersioner(vs);
      // Öppna alltid den senaste versionen — den färskaste synen på året.
      // B0 ligger kvar som jämförelsepunkt och väljs med ett klick.
      setVersion(vs.length ? vs[vs.length - 1].version : 'B0');
    })().catch((e) => console.error('budget versions:', e));
    return () => { live = false; };
  }, [bolag, ra]);

  // Ladda vald version
  useEffect(() => {
    let live = true;
    (async () => {
      const snap = await getDoc(doc(db, 'intel_budget', docId));
      if (!live) return;
      if (snap.exists()) {
        const d = snap.data();
        setRader(d.rader || {});
        setVeckor(d.veckor || {});
        setFoljUpp(d.foljUpp || {});
        setRadStatus(d.radStatus || {});
        setStatus(d.status || 'utkast');
        setForutsattningar(d.forutsattningar || '');
        setLikvid(d.likvid || {});
        setUtfallTom(d.utfallTom || '');
      } else {
        setRader({}); setVeckor({}); setFoljUpp({}); setRadStatus({}); setStatus('utkast'); setForutsattningar('');
        setLikvid({}); setUtfallTom('');
      }
      setVeckoRad(null); setDirty(false); setMsg('');
    })().catch((e) => console.error('budget load:', e));
    return () => { live = false; };
  }, [docId]);

  const last = status === 'faststalld';

  const setCell = (radId, ym, tkrVal) => {
    if (last) return;
    setRader((prev) => {
      const rad = { ...(prev[radId] || {}) };
      const v = Math.round((Number(tkrVal) || 0) * 1000);
      if (v === 0) delete rad[ym]; else rad[ym] = v;
      return { ...prev, [radId]: rad };
    });
    setDirty(true);
  };

  const spara = async (extra = {}) => {
    setSparar(true);
    try {
      await setDoc(doc(db, 'intel_budget', docId), {
        bolag, ra, version, status, rader, veckor, foljUpp, radStatus, forutsattningar, likvid, utfallTom,
        updatedAt: serverTimestamp(), updatedBy: auth.currentUser?.email || '?',
        ...extra,
      });
      setDirty(false);
      setMsg('Sparat ' + new Date().toLocaleTimeString('sv-SE').slice(0, 5));
      if (extra.status) setStatus(extra.status);
      if (!versioner.some((v) => v.version === version)) setVersioner((vs) => [...vs, { version, status: extra.status || status }]);
    } catch (e) {
      setMsg('FEL: ' + e.message);
    } finally { setSparar(false); }
  };

  const nyVersion = async () => {
    const nr = versioner.filter((v) => v.version.startsWith('P')).length + 1;
    const ny = `P${nr}`;
    // Kopiera ALLT som hör till budgeten, inte bara siffrorna. Missas
    // likvid tappar den nya versionen betalningsregler, förskott och
    // ingående saldon — och radStatus/foljUpp är arbetsläget man vill ha kvar.
    await setDoc(doc(db, 'intel_budget', `${bolag}_${ra}_${ny}`), {
      bolag, ra, version: ny, status: 'utkast', rader, veckor, foljUpp, radStatus, forutsattningar, likvid, utfallTom,
      updatedAt: serverTimestamp(), updatedBy: auth.currentUser?.email || '?',
    });
    setVersioner((vs) => [...vs, { version: ny, status: 'utkast' }]);
    setVersion(ny);
  };

  // ── Prognos: byt stängda månader mot bokfört utfall ─────────────
  // Bara i P-versioner — B0 är den fastställda budgeten och ska förbli
  // jämförelsepunkten. Varje kontogrupp stämmer exakt mot bokföringen;
  // se utfallPrognos.js för hur raderna fördelas inom gruppen.
  const fyllUtfall = async () => {
    if (last || !version.startsWith('P')) return;
    setFyllerUtfall(true);
    try {
      const nu = new Date();
      const nuYm = `${nu.getFullYear()}${String(nu.getMonth() + 1).padStart(2, '0')}`;
      const bokSnap = await getDocs(query(collection(db, 'intel_bokforing'), where('bolag', '==', bolag)));
      const bok = {};
      const allaDocs = [];
      bokSnap.forEach((d2) => {
        const v = d2.data();
        if (!v.ym || !v.summor || !Array.isArray(v.rader)) return;
        allaDocs.push(v);
        bok[v.ym] = v;
      });
      // Bara avslutade månader i året som har bokföring
      const man = manader.filter((m) => m < nuYm && bok[m]);
      if (!man.length) {
        setMsg(`Ingen bokföring för ${raLabel2(ra)} är uppladdad för ${BOLAG.find((b) => b.id === bolag)?.namn} — inget att fylla i.`);
        return;
      }
      const etikett = (m) => `${MANAD_LABELS[manader.indexOf(m)]} ${m.slice(2, 4)}`;
      const luckor = manader.filter((m) => m < man[man.length - 1] && !bok[m]);
      const ok = window.confirm(
        `Lägg in bokfört utfall i ${version}\n\n`
        + `Månader: ${man.map(etikett).join(', ')}\n\n`
        + 'Resultatraderna för dessa månader ersätts med utfall — även rader markerade klara (●), '
        + 'eftersom varje grupp måste gå ihop med bokföringen. Övriga månader och likvidraderna rörs inte.\n\n'
        + (luckor.length ? `OBS: bokföring saknas för ${luckor.map(etikett).join(', ')} — de förblir budget.\n\n` : '')
        + 'Fortsätt?',
      );
      if (!ok) return;

      // Fast/rörlig per konto: samma klassning som kostnadsanalysen,
      // bedömd över all uppladdad historik, med manuella överskrivningar.
      const typDoc = await getDoc(doc(db, 'intel_bokforing', `${bolag}_kontotyper`));
      const override = typDoc.exists() ? (typDoc.data().typer || {}) : {};
      const perKonto = {};
      for (const v of allaDocs) {
        for (const r of v.rader) {
          if (kontoGrupp(r.konto) !== 'ovrigt') continue;
          (perKonto[r.konto] || (perKonto[r.konto] = [])).push(r.belopp);
        }
      }
      const arFast = (konto) => (override[konto] || autoTyp(perKonto[konto] || [])) === 'fast';

      let agg = {};
      let lanthandel = {};
      if (bolag === 'stigmyhrs') {
        const lh = await getLanthandel();
        const momsFaktor = 1 + (lh?.moms?.sats || 0) / 100;
        for (const [datum, oms] of (lh?.days || [])) {
          const ym = datum.slice(0, 4) + datum.slice(5, 7);
          lanthandel[ym] = (lanthandel[ym] || 0) + oms / momsFaktor;
        }
      } else {
        const units = await getDailyUnits();
        for (const u of units) {
          const ym = u.datum.slice(0, 4) + u.datum.slice(5, 7);
          const e = agg[u.enhet] || (agg[u.enhet] = {});
          const m = e[ym] || (e[ym] = { intakt: 0, vk: 0, lon: 0 });
          m.intakt += u.intakt || 0;
          m.vk += Math.max(Math.abs(u.kostnad || 0) - Math.abs(u.lon || 0), 0);
          m.lon += Math.abs(u.lon || 0);
        }
      }

      const utfall = utfallTillRader({ bolag, raderDef, man, bok, agg, lanthandel, kontoGrupp, arFast });
      setRader((prev) => slaIhop(prev, utfall));
      setUtfallTom(man[man.length - 1]);
      setDirty(true);
      setMsg(`Utfall inlagt för ${man.map(etikett).join(', ')} — varje grupp stämmer mot bokföringen. `
        + 'Säsongspersonal är nollad för utfallsmånaderna (Qlik skiljer inte fast från säsong). Granska och spara.');
    } catch (e) {
      setMsg('FEL: ' + e.message);
    } finally { setFyllerUtfall(false); }
  };

  // ── Generator: utfall ELLER tidigare års budget × uppräknings-% ──
  const [genKalla, setGenKalla] = useState('utfall'); // 'utfall' | 'budget'
  const [genKallaRa, setGenKallaRa] = useState(raVal()[1]);
  const [genKallaVersion, setGenKallaVersion] = useState('B0');
  const [genKallaVersioner, setGenKallaVersioner] = useState(null); // null = ej laddat än
  const [upp, setUpp] = useState({ intakt: 6.5, vk: 3, personal: 3 });
  const [genJobb, setGenJobb] = useState(false);

  // Vilka versioner finns att utgå från i källåret? Hämtas först när panelen
  // står i budgetläge — annars en Firestore-fråga per årsklick i onödan.
  useEffect(() => {
    if (!genOpen || genKalla !== 'budget') return undefined;
    let live = true;
    setGenKallaVersioner(null);
    (async () => {
      const q1 = query(collection(db, 'intel_budget'), where('bolag', '==', bolag), where('ra', '==', genKallaRa));
      const snap = await getDocs(q1);
      const vs = snap.docs.map((d) => ({ version: d.data().version, status: d.data().status }))
        .sort((a, b) => a.version.localeCompare(b.version));
      if (!live) return;
      setGenKallaVersioner(vs);
      setGenKallaVersion((cur) => (vs.some((v) => v.version === cur) ? cur : (vs[0]?.version || 'B0')));
    })().catch((e) => console.error('budget källversioner:', e));
    return () => { live = false; };
  }, [genOpen, genKalla, bolag, genKallaRa]);

  const namnAv = (id) => raderDef.find((r) => r.id === id)?.label || id;
  const listaRader = (ids) => {
    const n = ids.slice(0, 8).map((id) => namnAv(id) + (radStatus[id] === 'pagar' ? ' ◐' : '')).join(', ');
    return ids.length > 8 ? `${n} … och ${ids.length - 8} till` : n;
  };

  // ── Generator B: ett tidigare års budget som utgångspunkt ────────
  // Utfallsgeneratorn duger inte när nästa års budget ska läggas: RÅ 26/27
  // har bara ett par månader i böckerna, och 25/26 kastar bort allt
  // handarbete som lades på mellanåret. Här kopieras i stället en sparad
  // budget månad för månad — position i räkenskapsåret, jul mot jul — med
  // uppräkning. Uppräkning 0 % = ren kopia av året innan.
  const genereraFranBudget = async () => {
    setGenJobb(true);
    try {
      const snap = await getDoc(doc(db, 'intel_budget', `${bolag}_${genKallaRa}_${genKallaVersion}`));
      if (!snap.exists()) {
        setMsg(`Ingen budget finns för ${raLabel2(genKallaRa)} ${genKallaVersion} — välj ett annat år eller en annan version.`);
        return;
      }
      const kalla = snap.data();
      const kallaRader = kalla.rader || {};
      const kallaMan = raManader(genKallaRa);
      // Bara rader som faktiskt har siffror i källan. En tom källrad ska
      // lämna målraden i fred, inte nolla den.
      const malRader = raderDef.filter((r) => kallaMan.some((km) => (kallaRader[r.id] || {})[km])).map((r) => r.id);
      const skyddade = malRader.filter((id) => radStatus[id] === 'klar');
      const fylls = malRader.filter((id) => radStatus[id] !== 'klar');
      if (!fylls.length) {
        setMsg(malRader.length
          ? 'Alla rader med siffror i källbudgeten är markerade klara (●) — inget fylldes i.'
          : `Budgeten för ${raLabel2(genKallaRa)} ${genKallaVersion} är tom — inget att kopiera.`);
        return;
      }
      // Samma fördelning som utfallsgeneratorn: övriga kostnader följer de
      // direkta, medan avskrivningar, räntor och likvidposter kopieras rakt
      // av — de styrs av anläggningsplan och lån, inte av prisutvecklingen.
      const pctAv = (grupp) => (grupp === 'intakt' ? upp.intakt
        : (grupp === 'vk' || grupp === 'ovrigt') ? upp.vk
          : grupp === 'personal' ? upp.personal : 0);
      // Betalningsreglerna är årsoberoende och sega att sätta om för hand.
      // Kopieras bara till ett målår som saknar egna — aldrig över befintliga.
      const kallRegler = kalla.likvid?.betalregler || {};
      const taRegler = !!Object.keys(kallRegler).length && !Object.keys(likvid?.betalregler || {}).length;

      const bekraftelse = `Generera förslag från BUDGET ${raLabel2(genKallaRa)} ${genKallaVersion}\n\n`
        + `SKRIVS ÖVER (${fylls.length} rader):\n  ${listaRader(fylls)}\n\n`
        + (skyddade.length
          ? `HOPPAS ÖVER — markerade klara (${skyddade.length} rader):\n  ${listaRader(skyddade)}\n\n`
          : '')
        + `Uppräkning: intäkter +${upp.intakt} %, direkta och övriga +${upp.vk} %, personal +${upp.personal} %. `
        + 'Avskrivningar, räntor och likvidposter kopieras rakt av.\n\n'
        + (taRegler ? 'Betalningsreglerna följer med (målåret har inga egna).\n\n' : '')
        + 'Veckobudgetar, förskott och ingående saldon följer INTE med.\n\n'
        + 'Fortsätt?';
      if (!window.confirm(bekraftelse)) return;

      const nya = { ...rader };
      for (const id of fylls) {
        const pct = pctAv(raderDef.find((r) => r.id === id).grupp);
        const rad = {};
        kallaMan.forEach((km, i) => {
          const v = Math.round(((kallaRader[id] || {})[km] || 0) * (1 + pct / 100));
          if (v !== 0) rad[manader[i]] = v;
        });
        nya[id] = rad;
      }
      setRader(nya);
      if (taRegler) setLikvid((prev) => ({ ...prev, betalregler: { ...kallRegler } }));
      setDirty(true);
      setGenOpen(false);
      const rentKopia = !upp.intakt && !upp.vk && !upp.personal;
      setMsg(`${rentKopia ? 'Kopierat' : 'Förslag genererat'} från budget ${raLabel2(genKallaRa)} ${genKallaVersion}`
        + `${rentKopia ? ' rakt av (0 % uppräkning)' : ` med uppräkning intäkter +${upp.intakt} %, direkta +${upp.vk} %, personal +${upp.personal} %`}`
        + ` — ${fylls.length} rader fyllda`
        + `${skyddade.length ? `, ${skyddade.length} klarmarkerade orörda` : ''}`
        + `${taRegler ? ', betalningsreglerna följde med' : ''}`
        + '. Justera och spara.');
    } catch (e) {
      setMsg('Generator-fel: ' + e.message);
    } finally { setGenJobb(false); }
  };

  const generera = async () => {
    // Rader generatorn täcker — beräknas i förväg så bekräftelsen kan visa
    // exakt vad som skrivs över. Klarmarkerade rader (●) skyddas alltid.
    const malRader = bolag === 'stigmyhrs'
      ? ['intakt:lanthandel', 'intakt:huvudbutik', 'vk:huvudbutik', 'vk:lanthandel',
        'ovrigt:fasta', 'ovrigt:ovriga', 'personal:fast', 'avskrivningar', 'rantor']
      : [
        ...raderDef.filter((r) => (r.grupp === 'intakt' || r.grupp === 'vk') && r.qlikEnhet).map((r) => r.id),
        'personal:fast', 'personal:oh',
        // Ur bokföringen — Qlik saknar dem helt eller bara delvis
        'intakt:ovriga', 'vk:ovriga', 'ovrigt:fasta', 'ovrigt:ovriga', 'avskrivningar', 'rantor',
      ];
    const finns = (id) => raderDef.some((r) => r.id === id);
    const skyddade = malRader.filter((id) => finns(id) && radStatus[id] === 'klar');
    const fylls = malRader.filter((id) => finns(id) && radStatus[id] !== 'klar');

    if (!fylls.length) {
      setGenOpen(false);
      setMsg('Alla rader generatorn täcker är markerade klara (●) — inget fylldes i.');
      return;
    }
    // Samma källår som budgetår = man fyller i historik. Då är uppräkningen
    // nästan alltid fel, så påminn om den innan något skrivs över.
    const uppSatt = upp.intakt || upp.vk || upp.personal;
    const bekraftelse = `Generera förslag från utfall ${raLabel2(genKallaRa)}\n\n`
      + `SKRIVS ÖVER (${fylls.length} rader):\n  ${listaRader(fylls)}\n\n`
      + (skyddade.length
        ? `HOPPAS ÖVER — markerade klara (${skyddade.length} rader):\n  ${listaRader(skyddade)}\n\n`
        : '')
      + (genKallaRa === ra && uppSatt
        ? `OBS: källåret är samma som budgetåret, men uppräkningen är påslagen `
          + `(intäkter +${upp.intakt} %). Vill du ha rent utfall — sätt uppräkningarna till 0 först.\n\n`
        : '')
      + 'Fortsätt?';
    if (!window.confirm(bekraftelse)) return;

    const skydda = new Set(skyddade);
    setGenJobb(true);
    try {
      const kallaMan = raManader(genKallaRa);
      const nya = { ...rader };
      const fyll = (radId, manadsKr, pct) => {
        if (skydda.has(radId)) return; // klarmarkerad rad — rörs inte
        const rad = {};
        kallaMan.forEach((km, i) => {
          const v = Math.round((manadsKr[km] || 0) * (1 + pct / 100));
          if (v !== 0) rad[manader[i]] = v;
        });
        nya[radId] = rad;
      };
      // Saknas underlaget helt (ingen SIE uppladdad för året) ska raden lämnas
      // som den är — att nolla den vore att tyst radera handpålagt arbete.
      const utanUnderlag = [];
      const fyllOmFinns = (radId, manadsKr, pct) => {
        if (kallaMan.some((km) => manadsKr[km])) fyll(radId, manadsKr, pct);
        else utanUnderlag.push(radId);
      };

      // ── Bokföringen: gemensam för alla bolag ──────────────────────
      // Qlik känner bara resultatenheterna. Övriga kostnader, avskrivningar,
      // räntor och intäkter som aldrig passerar kassasystemen finns bara här.
      const bokSnap = await getDocs(query(collection(db, 'intel_bokforing'), where('bolag', '==', bolag)));
      const manDocs = [];
      bokSnap.forEach((d2) => {
        const v = d2.data();
        if (v.ym && v.summor && Array.isArray(v.rader)) manDocs.push(v);
      });
      const perGrupp = (falt) => {
        const ut = {};
        for (const v of manDocs) ut[v.ym] = Math.round(v.summor[falt] || 0);
        return ut;
      };
      // Övriga kostnader delas på fast/rörligt med samma klassning som
      // kostnadsanalysen visar, inklusive de manuella överskrivningarna.
      const delaOvrigt = async () => {
        const typDoc = await getDoc(doc(db, 'intel_bokforing', `${bolag}_kontotyper`));
        const override = typDoc.exists() ? (typDoc.data().typer || {}) : {};
        const perKonto = {};
        for (const v of manDocs) {
          for (const r of v.rader) {
            if (kontoGrupp(r.konto) !== 'ovrigt') continue;
            const k = perKonto[r.konto] || (perKonto[r.konto] = {});
            k[v.ym] = (k[v.ym] || 0) + r.belopp;
          }
        }
        const fastMan = {}, ovrMan = {};
        for (const [konto, byYm] of Object.entries(perKonto)) {
          const mal = (override[konto] || autoTyp(Object.values(byYm))) === 'fast' ? fastMan : ovrMan;
          for (const [ym, kr] of Object.entries(byYm)) mal[ym] = Math.round((mal[ym] || 0) + kr);
        }
        return { fastMan, ovrMan };
      };

      if (bolag === 'stigmyhrs') {
        // Lanthandeln: intäkt från HonestBox-historiken (dagar → månader).
        // HonestBox levererar INKL moms medan budgeten är ex moms, så allt
        // räknas om med den blandade satsen ur artikelraderna. Det gäller
        // dubbelt här: samma belopp dras strax nedan av från bokförd intäkt,
        // som redan är netto — brutto mot netto hade lagt momsen på fel sida
        // två gånger.
        const lh = await getLanthandel();
        const momsFaktor = 1 + (lh?.moms?.sats || 0) / 100;
        const perMan = {};
        for (const [datum, oms] of (lh?.days || [])) {
          const ym = datum.slice(0, 4) + datum.slice(5, 7);
          perMan[ym] = (perMan[ym] || 0) + oms / momsFaktor;
        }
        fyll('intakt:lanthandel', perMan, upp.intakt);
        // Huvudbutiken: bokförd intäkt (SIE-uppladdningen) minus Lanthandelns
        // kassaoms — det som blir kvar är Ramundbergets-butiken
        const huvudMan = {};
        for (const v of manDocs) {
          huvudMan[v.ym] = Math.max(Math.round((v.summor.intakt || 0) - (perMan[v.ym] || 0)), 0);
        }
        fyllOmFinns('intakt:huvudbutik', huvudMan, upp.intakt);

        // Varukostnaden delas mellan butikerna i förhållande till deras intäkt;
        // kostnadsställena är för glest kodade för att gå att lita på (majoriteten
        // av verifikatraderna saknar KS).
        const vkTot = perGrupp('vk');
        const andel = (ym) => {
          const lh = perMan[ym] || 0;
          const hb = huvudMan[ym] || 0;
          return hb + lh > 0 ? lh / (hb + lh) : 0;
        };
        const vkLh = {}, vkHb = {};
        for (const [ym, kr] of Object.entries(vkTot)) {
          vkLh[ym] = Math.round(kr * andel(ym));
          vkHb[ym] = kr - vkLh[ym];
        }
        fyllOmFinns('vk:huvudbutik', vkHb, upp.vk);
        fyllOmFinns('vk:lanthandel', vkLh, upp.vk);
        fyllOmFinns('personal:fast', perGrupp('personal'), upp.personal);
      } else {
        // Qlik daily_units: intäkt, vk (=kostnad−lön) och lön per enhet×månad
        const units = await getDailyUnits();
        const agg = {}; // enhet → ym → { intakt, vk, lon }
        for (const u of units) {
          const ym = u.datum.slice(0, 4) + u.datum.slice(5, 7);
          const e = agg[u.enhet] || (agg[u.enhet] = {});
          const m = e[ym] || (e[ym] = { intakt: 0, vk: 0, lon: 0 });
          m.intakt += u.intakt || 0;
          m.vk += Math.max(Math.abs(u.kostnad || 0) - Math.abs(u.lon || 0), 0);
          m.lon += Math.abs(u.lon || 0);
        }
        const pick = (enhet, falt) => {
          const src = agg[enhet] || {};
          const out = {};
          for (const [ym, v] of Object.entries(src)) out[ym] = v[falt];
          return out;
        };
        for (const r of raderDef) {
          if (r.grupp === 'intakt' && r.qlikEnhet) fyll(r.id, pick(r.qlikEnhet, 'intakt'), upp.intakt);
          if (r.grupp === 'vk' && r.qlikEnhet)     fyll(r.id, pick(r.qlikEnhet, 'vk'), upp.vk);
        }
        // Personal: total lön (alla bolagets enheter) → Fast personal (justera/splitta manuellt)
        const enhSet = new Set(raderDef.filter((r) => r.qlikEnhet).map((r) => r.qlikEnhet));
        if (bolag === 'hotell') { enhSet.add('OH'); enhSet.add('Saknas'); enhSet.delete('Sportcenter'); }
        // O/H-enhetens lön hamnar på egen rad. Annars skulle ledningslönerna
        // ligga i Fast personal och O/H-utbrytningen i styrelserapporten
        // dubbelräkna dem så snart generatorn körts om.
        const lonMan = {}, lonOh = {};
        for (const [enhet, byYm] of Object.entries(agg)) {
          if (!enhSet.has(enhet)) continue;
          const mal = enhet === 'OH' ? lonOh : lonMan;
          for (const [ym, v] of Object.entries(byYm)) mal[ym] = (mal[ym] || 0) + v.lon;
        }
        fyll('personal:fast', lonMan, upp.personal);
        fyllOmFinns('personal:oh', lonOh, upp.personal);

        // Avstämning mot bokföringen: bokförd 3xxx/4xxx minus Qliks
        // resultatenheter. På intäktssidan är det oftast riktiga sidointäkter
        // (hyror, uthyrd arbetskraft, ersättningar); på kostnadssidan mest
        // periodisering — Qlik tar varukostnaden när varan säljs, bokföringen
        // när fakturan kommer. Får därför vara NEGATIV: klämmer man den till
        // noll stämmer inte längre budgetens totalsumma mot böckerna.
        const summaQlik = (falt) => {
          const ut = {};
          for (const [enhet, byYm] of Object.entries(agg)) {
            if (!enhSet.has(enhet)) continue;
            for (const [ym, v] of Object.entries(byYm)) ut[ym] = (ut[ym] || 0) + v[falt];
          }
          return ut;
        };
        const intaktQlik = summaQlik('intakt');
        const vkQlik = summaQlik('vk');
        const restIntakt = {}, restVk = {};
        for (const v of manDocs) {
          restIntakt[v.ym] = Math.round((v.summor.intakt || 0) - (intaktQlik[v.ym] || 0));
          restVk[v.ym] = Math.round((v.summor.vk || 0) - (vkQlik[v.ym] || 0));
        }
        fyllOmFinns('intakt:ovriga', restIntakt, upp.intakt);
        fyllOmFinns('vk:ovriga', restVk, upp.vk);
      }

      // Gemensamt för alla bolag: det Qlik inte känner till alls
      const { fastMan, ovrMan } = await delaOvrigt();
      fyllOmFinns('ovrigt:fasta', fastMan, upp.vk);
      fyllOmFinns('ovrigt:ovriga', ovrMan, upp.vk);
      // Avskrivningar följer anläggningsplanen och räntorna lånet — ingen
      // uppräkning, de har inget med prisutvecklingen att göra.
      fyllOmFinns('avskrivningar', perGrupp('avskr'), 0);
      fyllOmFinns('rantor', perGrupp('finans'), 0);

      setRader(nya);
      setDirty(true);
      setGenOpen(false);
      // Skriv ut uppräkningen — annars är det lätt att tro att siffrorna är
      // rent utfall, särskilt när man fyller i ett år som redan passerat.
      const uppTxt = bolag === 'stigmyhrs'
        ? `+${upp.intakt} %`
        : `intäkter +${upp.intakt} %, direkta +${upp.vk} %, personal +${upp.personal} %`;
      const saknas = utanUnderlag.filter((id) => !skydda.has(id));
      setMsg(`Förslag genererat från utfall ${raLabel2(genKallaRa)} med uppräkning ${uppTxt} — `
        + `${fylls.length - saknas.length} rader fyllda`
        + `${skyddade.length ? `, ${skyddade.length} klarmarkerade orörda` : ''}`
        + `${saknas.length ? `. Saknar underlag i bokföringen och lämnades orörda: ${saknas.map(namnAv).join(', ')}` : ''}`
        + '. Justera och spara.');
    } catch (e) {
      setMsg('Generator-fel: ' + e.message);
    } finally { setGenJobb(false); }
  };

  // ── Beräkningar ──────────────────────────────────────────────────
  const S = useMemo(() => summering(rader, raderDef, manader), [rader, raderDef, manader]);
  const radSum = (radId) => manader.reduce((s, m) => s + ((rader[radId] || {})[m] || 0), 0);
  let ack = 0;
  const ackPerMan = manader.map((m) => (ack += S[m].resultat));

  // ── Render ───────────────────────────────────────────────────────
  const cellInp = {
    width: 56, border: '1px solid var(--border)', borderRadius: 5, padding: '2px 4px',
    fontSize: 11, textAlign: 'right', fontFamily: 'system-ui,sans-serif',
    background: last ? 'var(--panel)' : 'var(--card)', color: 'var(--text)',
  };
  const th = { fontSize: 10, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', padding: '4px 3px', textAlign: 'right', fontFamily: 'system-ui,sans-serif' };
  const tdLabel = { fontSize: 11.5, padding: '2px 6px 2px 0', whiteSpace: 'nowrap', fontFamily: 'system-ui,sans-serif', position: 'sticky', left: 0, background: 'var(--card)', zIndex: 1 };
  const tdCalc = { fontSize: 11, padding: '3px 4px', textAlign: 'right', fontFamily: 'system-ui,sans-serif', whiteSpace: 'nowrap' };

  // ── Radstatus: klicka pricken för att cykla obehandlad → pågår → klar ──
  const cyklaStatus = (radId) => {
    if (last) return;
    setRadStatus((prev) => {
      const ut = { ...prev };
      const ny = prev[radId] === 'pagar' ? 'klar' : prev[radId] === 'klar' ? null : 'pagar';
      if (ny) ut[radId] = ny; else delete ut[radId];
      return ut;
    });
    setDirty(true);
  };
  // Sticky etikettcell har egen bakgrund — lägg tonen som gradient ovanpå
  const labelBg = (radId) => {
    const f = STATUS_FARG[radStatus[radId]];
    return f ? `linear-gradient(${f}, ${f}), var(--card)` : 'var(--card)';
  };
  const StatusPrick = ({ radId }) => {
    const s = radStatus[radId];
    return (
      <button onClick={() => cyklaStatus(radId)} disabled={last}
        title={s === 'klar' ? 'Klar — klicka för att rensa' : s === 'pagar' ? 'Pågår — klicka för klar' : 'Markera som påbörjad'}
        style={{ background: 'transparent', border: 'none', padding: '0 5px 0 0', fontSize: 10, lineHeight: 1,
          cursor: last ? 'default' : 'pointer', color: STATUS_TON[s] || 'var(--border)' }}>
        {STATUS_PRICK[s] || '○'}
      </button>
    );
  };
  // ── Schablonknapp på gruppen "Direkta kostnader" ──────────────────
  // Lägger ut varukostnad = schablon-% × respektive intäktsrad, för alla
  // vk-rader som har en schablon. Klarmarkerade rader skyddas som vanligt.
  const schablonRader = Object.entries(VK_SCHABLON).filter(([id]) => raderDef.some((r) => r.id === id));
  const fyllSchabloner = () => {
    const skyddade = schablonRader.filter(([id]) => radStatus[id] === 'klar');
    const fylls = schablonRader.filter(([id]) => radStatus[id] !== 'klar');
    const utan = raderDef.filter((r) => r.grupp === 'vk' && !VK_SCHABLON[r.id]);
    if (!fylls.length) {
      setMsg('Alla varukostnadsrader med schablon är markerade klara (●) — inget fylldes i.');
      return;
    }
    const text = `Lägger ut schablon på varukostnaderna:\n\n`
      + fylls.map(([id, s]) => `  ${namnAv(id)}: ${Math.round(s.pct * 100)} % av ${namnAv(s.intaktRad)}`).join('\n')
      + '\n\n'
      + (skyddade.length ? `Hoppas över — markerade klara: ${skyddade.map(([id]) => namnAv(id)).join(', ')}\n\n` : '')
      + (utan.length ? `Saknar schablon, rörs inte: ${utan.map((r) => r.label).join(', ')}\n\n` : '')
      + 'Befintliga värden på raderna ovan skrivs över. Fortsätt?';
    if (!window.confirm(text)) return;

    setRader((prev) => {
      const nya = { ...prev };
      for (const [vkId, s] of fylls) {
        const int = prev[s.intaktRad] || {};
        const rad = {};
        for (const m of manader) {
          const v = Math.round((int[m] || 0) * s.pct);
          if (v) rad[m] = v;
        }
        nya[vkId] = rad;
      }
      return nya;
    });
    setDirty(true);
    setMsg(`Schablon utlagd på ${fylls.length} varukostnadsrader — justera och spara.`);
  };

  const antalKlara = raderDef.filter((r) => radStatus[r.id] === 'klar').length;
  const antalPagar = raderDef.filter((r) => radStatus[r.id] === 'pagar').length;

  const grupper = ['intakt', 'vk', 'ovrigt', 'personal'];
  const allaIhop = grupper.every((g) => ihop[g]);
  // Månader där prognosversionen bär bokfört utfall — tonas så att det syns
  // direkt var budgeten slutar och verkligheten tar vid. Tonen läggs som
  // backgroundImage så att radens statusfärg fortfarande lyser igenom.
  const arUtfall = (m) => !!utfallTom && m <= utfallTom;
  const UTFALL_TON = 'rgba(46, 110, 142, 0.13)';
  const utfallCell = (m) => (arUtfall(m) ? { backgroundImage: `linear-gradient(${UTFALL_TON}, ${UTFALL_TON})` } : null);
  const utfallInp = (m) => (arUtfall(m) ? { background: `linear-gradient(${UTFALL_TON}, ${UTFALL_TON}), ${last ? 'var(--panel)' : 'var(--card)'}` } : null);

  const calcRow = (label, vals, opts = {}) => (
    <tr key={label} style={{ background: opts.bg || 'var(--panel)', fontWeight: 700, cursor: opts.onClick ? 'pointer' : 'default' }}
      onClick={opts.onClick}>
      <td style={{ ...tdLabel, background: opts.bg || 'var(--panel)', fontWeight: 700 }}>
        {opts.pil != null && <span style={{ display: 'inline-block', width: 14, color: 'var(--text-secondary)' }}>{opts.pil ? '▸' : '▾'}</span>}
        {label}
        {opts.extra}
      </td>
      {vals.map((v, i) => (
        <td key={i} style={{ ...tdCalc, ...utfallCell(manader[i]), color: v < 0 ? '#A32D2D' : 'inherit' }}>{kr0(v).toLocaleString('sv-SE')}</td>
      ))}
      {/* Ackumulerade rader saknar meningsfull årssumma (radens sista värde ÄR året) */}
      {opts.utanSumma ? (
        <td style={{ ...tdCalc, color: 'var(--text-secondary)', fontWeight: 400 }}>–</td>
      ) : (
        <td style={{ ...tdCalc, fontWeight: 700, color: vals.reduce((a, b) => a + b, 0) < 0 ? '#A32D2D' : 'inherit' }}>
          {kr0(vals.reduce((a, b) => a + b, 0)).toLocaleString('sv-SE')}
        </td>
      )}
    </tr>
  );

  return (
    <>
      {/* Väljare + åtgärder */}
      <div className="card" style={{ paddingTop: 12, paddingBottom: 12 }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', fontFamily: 'system-ui,sans-serif' }}>
          {raVal().map((r) => <button key={r} onClick={() => setRa(r)} style={btn(ra === r)}>{raLabel2(r)}</button>)}
          <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--border)', margin: '0 4px' }} />
          {BOLAG.map((b) => <button key={b.id} onClick={() => setBolag(b.id)} style={btn(bolag === b.id)}>{b.namn.replace(' AB', '')}</button>)}
          <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--border)', margin: '0 4px' }} />
          {(versioner.length ? versioner : [{ version: 'B0', status: 'utkast' }]).map((v) => (
            <button key={v.version} onClick={() => setVersion(v.version)} style={btn(version === v.version)}>
              {v.version}{v.status === 'faststalld' ? ' 🔒' : ''}
            </button>
          ))}
          <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--border)', margin: '0 4px' }} />
          {[['budget', 'Budgetmatris'], ['likviditet', 'Likviditetsmatris'], ['utfall', 'Utfall']].map(([id, l]) => (
            <button key={id} onClick={() => setVy(id)} style={btn(vy === id)}>{l}</button>
          ))}
          <div style={{ flex: 1 }} />
          {vy === 'budget' && isAdmin && <button onClick={() => setGenOpen((o) => !o)} style={actionBtn} disabled={last}>⚡ Generera förslag</button>}
          {vy === 'budget' && isAdmin && version.startsWith('P') && !last && (
            <button onClick={fyllUtfall} disabled={fyllerUtfall} style={actionBtn}
              title="Byt de stängda månaderna mot bokfört utfall — resten av året förblir budget">
              {fyllerUtfall ? 'Hämtar utfall…' : '📒 Lägg in utfall'}
            </button>
          )}
          <button onClick={nyVersion} style={actionBtn}>+ Ny version</button>
          {!last && <button onClick={() => spara({ status: 'faststalld' })} style={actionBtn}>🔒 Fastställ</button>}
          {last && <button onClick={() => spara({ status: 'utkast' })} style={actionBtn}>🔓 Lås upp</button>}
          <button onClick={() => spara()} disabled={!dirty || sparar || last}
            style={{ ...actionBtn, background: dirty && !last ? ACCENT : 'transparent', color: dirty && !last ? '#fff' : 'var(--text-secondary)' }}>
            {sparar ? 'Sparar…' : 'Spara'}
          </button>
        </div>
        {msg && <div style={{ marginTop: 8, fontSize: 12, color: msg.startsWith('FEL') || msg.startsWith('Generator-fel') ? '#A32D2D' : '#3B6D11', fontFamily: 'system-ui,sans-serif' }}>{msg}</div>}

        {genOpen && !last && (
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', fontFamily: 'system-ui,sans-serif', fontSize: 12 }}>
            <span>Utgå från</span>
            {[['utfall', 'Utfall'], ['budget', 'Budget']].map(([id, l]) => (
              <button key={id} onClick={() => setGenKalla(id)} style={btn(genKalla === id)}>{l}</button>
            ))}
            {raVal().slice(1).map((r) => <button key={r} onClick={() => setGenKallaRa(r)} style={btn(genKallaRa === r)}>{raLabel2(r)}</button>)}
            {genKalla === 'budget' && (genKallaVersioner === null
              ? <span style={{ color: 'var(--text-secondary)' }}>hämtar versioner…</span>
              : genKallaVersioner.length
                ? genKallaVersioner.map((v) => (
                  <button key={v.version} onClick={() => setGenKallaVersion(v.version)} style={btn(genKallaVersion === v.version)}>
                    {v.version}{v.status === 'faststalld' ? ' 🔒' : ''}
                  </button>
                ))
                : <span style={{ color: '#A32D2D' }}>ingen sparad budget för {raLabel2(genKallaRa)}</span>)}
            <span style={{ marginLeft: 6 }}>Intäkter %</span>
            <input type="number" step="0.5" value={upp.intakt} onChange={(e) => setUpp({ ...upp, intakt: Number(e.target.value) })} style={{ ...cellInp, width: 52 }} />
            <span>Direkta kostn. %</span>
            <input type="number" step="0.5" value={upp.vk} onChange={(e) => setUpp({ ...upp, vk: Number(e.target.value) })} style={{ ...cellInp, width: 52 }} />
            <span>Personal %</span>
            <input type="number" step="0.5" value={upp.personal} onChange={(e) => setUpp({ ...upp, personal: Number(e.target.value) })} style={{ ...cellInp, width: 52 }} />
            <button onClick={() => setUpp({ intakt: 0, vk: 0, personal: 0 })} style={btn(!upp.intakt && !upp.vk && !upp.personal)}
              title="Nolla uppräkningen — kopierar källåret rakt av">0 %</button>
            <button onClick={genKalla === 'budget' ? genereraFranBudget : generera}
              disabled={genJobb || (genKalla === 'budget' && !(genKallaVersioner || []).length)} style={{ ...actionBtn, background: ACCENT, color: '#fff' }}>{genJobb ? 'Genererar…' : 'Generera'}</button>
            <span style={{ color: 'var(--text-secondary)', fontSize: 11 }}>
              {genKalla === 'budget'
                ? <>Kopierar <strong>alla rader med siffror</strong> ur den valda budgeten, månad mot månad i räkenskapsåret (jul mot jul), med uppräkningen ovan. Avskrivningar, räntor och likvidposter kopieras rakt av. <strong>0 %</strong> ger en ren kopia av året innan. Veckobudgetar, förskott och ingående saldon följer inte med.</>
                : <>Fyller intäkter, direkta kostnader{bolag !== 'stigmyhrs' ? ' och personal (allt i Fast — splitta själv)' : ''} ur Qlik, samt övriga kostnader, avskrivningar och räntor ur den inlästa bokföringen. Rader utan underlag lämnas orörda.</>}
              {' '}Skriver över befintliga värden på de raderna — <strong>utom rader markerade klara (●)</strong>, som alltid hoppas över. Du får en sammanställning att bekräfta först.
            </span>
          </div>
        )}
      </div>

      {vy === 'utfall' && (
        <UtfallsMatris bolag={bolag} ra={ra} manader={manader} rader={rader} />
      )}

      {vy === 'likviditet' && (
        <LikviditetsMatris
          bolag={bolag} ra={ra} manader={manader} rader={rader} likvid={likvid} last={last}
          onSetCell={setCell}
          onSetLikvid={(l) => { setLikvid(l); setDirty(true); }}
        />
      )}

      {/* Matrisen */}
      <div className="card" style={{ display: vy === 'budget' ? undefined : 'none' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
          <div className="card-title" style={{ marginBottom: 0 }}>
            Budget {raLabel2(ra)} — {BOLAG.find((b) => b.id === bolag)?.namn} · {version}{last ? ' (fastställd)' : ''} · tkr
            {utfallTom && (
              <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-secondary)', marginLeft: 10, textTransform: 'none', letterSpacing: 0 }}>
                <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: UTFALL_TON, border: '1px solid rgba(46, 110, 142, 0.35)', verticalAlign: -1, marginRight: 4 }} />
                utfall t.o.m. {MANAD_LABELS[manader.indexOf(utfallTom)]} {utfallTom.slice(2, 4)}
              </span>
            )}
            {(antalKlara > 0 || antalPagar > 0) && (
              <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-secondary)', marginLeft: 10, textTransform: 'none', letterSpacing: 0 }}>
                <span style={{ color: STATUS_TON.klar }}>●</span> {antalKlara} klara
                {antalPagar > 0 && <> · <span style={{ color: STATUS_TON.pagar }}>◐</span> {antalPagar} pågår</>}
                {' '}av {raderDef.length} rader
              </span>
            )}
          </div>
          <button onClick={() => setIhop(allaIhop ? {} : { intakt: true, vk: true, ovrigt: true, personal: true })} style={actionBtn}>
            {allaIhop ? '▾ Visa alla rader' : '▸ Kort resultatrapport'}
          </button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                <th style={{ ...th, textAlign: 'left', position: 'sticky', left: 0, background: 'var(--card)', zIndex: 2 }}></th>
                {MANAD_LABELS.map((m, i) => (
                  <th key={m} style={{ ...th, ...utfallCell(manader[i]) }}
                    title={arUtfall(manader[i]) ? 'Bokfört utfall' : 'Budget'}>
                    {m}
                    {arUtfall(manader[i]) && <div style={{ fontSize: 8.5, fontWeight: 600, letterSpacing: '.04em', color: '#2E6E8E' }}>UTFALL</div>}
                  </th>
                ))}
                <th style={th}>Summa</th>
              </tr>
            </thead>
            <tbody>
              {grupper.map((grupp) => (
                <React.Fragment key={grupp}>
                  {calcRow(GRUPP_LABELS[grupp], manader.map((m) => {
                    const g = { intakt: 'intakter', vk: 'direkta', ovrigt: 'ovriga', personal: 'personal' }[grupp];
                    return grupp === 'intakt' ? S[m][g] : -S[m][g];
                  }), {
                    pil: !!ihop[grupp],
                    onClick: () => setIhop((p) => ({ ...p, [grupp]: !p[grupp] })),
                    extra: last ? null
                      : grupp === 'vk' && schablonRader.length > 0 ? (
                        <button onClick={(e) => { e.stopPropagation(); fyllSchabloner(); }}
                          title="Lägg ut schablon-% av respektive intäktsrad på alla varukostnadsrader"
                          style={{ marginLeft: 8, background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: 5, padding: '1px 7px', fontSize: 10, fontWeight: 400, cursor: 'pointer', fontFamily: 'system-ui,sans-serif' }}>
                          ⚡ Schablon
                        </button>
                      ) : grupp === 'personal' && bolag !== 'stigmyhrs' && isAdmin ? (
                        <button onClick={(e) => { e.stopPropagation(); setPersonalPanel((o) => !o); }}
                          title="Hämta fast/säsong-uppdelningen från Ledning → Personal"
                          style={{ marginLeft: 8, background: personalPanel ? '#44365C' : 'transparent', color: personalPanel ? '#fff' : 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: 5, padding: '1px 7px', fontSize: 10, fontWeight: 400, cursor: 'pointer', fontFamily: 'system-ui,sans-serif' }}>
                          👥 Fast/säsong
                        </button>
                      ) : null,
                  })}
                  {!ihop[grupp] && raderDef.filter((r) => r.grupp === grupp).map((r) => (
                    <tr key={r.id} style={{ background: STATUS_FARG[radStatus[r.id]] || 'transparent' }}>
                      <td style={{ ...tdLabel, background: labelBg(r.id) }}>
                        <StatusPrick radId={r.id} />
                        {r.label}
                        {grupp === 'intakt' && !last && !r.ingenVecka && (
                          <button onClick={() => setVeckoRad(veckoRad === r.id ? null : r.id)}
                            title="Budgetera veckovis (med ifjol + bokningsläge som stöd)"
                            style={{ marginLeft: 5, background: veckoRad === r.id ? '#44365C' : 'transparent', color: veckoRad === r.id ? '#fff' : 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: 5, padding: '0 4px', fontSize: 10, cursor: 'pointer' }}>
                            🗓
                          </button>
                        )}
                        {!last && (
                          <button onClick={() => setAssistRad(assistRad === r.id ? null : r.id)}
                            title="Budgetassistent (AI): fråga, resonera och få förslag på raden"
                            style={{ marginLeft: 5, background: assistRad === r.id ? '#44365C' : 'transparent', color: assistRad === r.id ? '#fff' : 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: 5, padding: '0 4px', fontSize: 10, cursor: 'pointer' }}>
                            ✨
                          </button>
                        )}
                      </td>
                      {manader.map((m) => (
                        <td key={m} style={{ padding: '1px 2px', ...utfallCell(m) }}>
                          <input type="number" style={{ ...cellInp, ...utfallInp(m) }} disabled={last}
                            value={(rader[r.id] || {})[m] ? kr0(rader[r.id][m]) : ''}
                            placeholder="0"
                            onChange={(e) => setCell(r.id, m, e.target.value)} />
                        </td>
                      ))}
                      <td style={{ ...tdCalc, fontWeight: 600 }}>{kr0(radSum(r.id)).toLocaleString('sv-SE')}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
              {calcRow('Rörelsens kostnader', manader.map((m) => -S[m].rorelsensKostn))}
              {calcRow('Driftsresultat', manader.map((m) => S[m].driftsresultat), { bg: 'var(--surface-2)' })}
              {raderDef.filter((r) => r.grupp === 'avskr' || r.grupp === 'finans').map((r) => (
                <tr key={r.id} style={{ background: STATUS_FARG[radStatus[r.id]] || 'transparent' }}>
                  <td style={{ ...tdLabel, background: labelBg(r.id) }}>
                    <StatusPrick radId={r.id} />
                    {r.label}
                    {!last && (
                      <button onClick={() => setAssistRad(assistRad === r.id ? null : r.id)}
                        title="Budgetassistent (AI): fråga, resonera och få förslag på raden"
                        style={{ marginLeft: 5, background: assistRad === r.id ? '#44365C' : 'transparent', color: assistRad === r.id ? '#fff' : 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: 5, padding: '0 4px', fontSize: 10, cursor: 'pointer' }}>
                        ✨
                      </button>
                    )}
                  </td>
                  {manader.map((m) => (
                    <td key={m} style={{ padding: '1px 2px', ...utfallCell(m) }}>
                      <input type="number" style={{ ...cellInp, ...utfallInp(m) }} disabled={last}
                        value={(rader[r.id] || {})[m] ? kr0(rader[r.id][m]) : ''} placeholder="0"
                        onChange={(e) => setCell(r.id, m, e.target.value)} />
                    </td>
                  ))}
                  <td style={{ ...tdCalc, fontWeight: 600 }}>{kr0(radSum(r.id)).toLocaleString('sv-SE')}</td>
                </tr>
              ))}
              {calcRow('Resultat månad', manader.map((m) => S[m].resultat), { bg: 'var(--surface-2)' })}
              {calcRow('Ackumulerat resultat', ackPerMan, { utanSumma: true })}
            </tbody>
          </table>
        </div>
        <div className="note">
          Kostnader matas in som positiva tal (tkr) och räknas negativt i summeringarna. Summa-raden {`"${GRUPP_LABELS.intakt}"`} m.fl. beräknas live.
          Kassaflödet — amorteringar, investeringar och förskott — ligger under <strong>Likviditetsmatris</strong>.
        </div>
      </div>

      {/* Veckobudget-panelen (A+B: veckoinmatning med ifjol + orderboken som stöd) */}
      {veckoRad && !last && (
        <VeckoBudget
          ra={ra} bolag={bolag}
          radDef={raderDef.find((r) => r.id === veckoRad)}
          raderDef={raderDef} rader={rader} veckorAlla={veckor}
          varden={veckor[veckoRad] || {}}
          foljUpp={foljUpp[veckoRad] || {}}
          onToggleFoljUpp={(k) => {
            setFoljUpp((prev) => {
              const rad = { ...(prev[veckoRad] || {}) };
              if (rad[k]) delete rad[k]; else rad[k] = true;
              return { ...prev, [veckoRad]: rad };
            });
            setDirty(true);
          }}
          onSetVecka={(k, krBelopp) => {
            setVeckor((p) => {
              const rad = { ...(p[veckoRad] || {}) };
              if (krBelopp) rad[k] = krBelopp; else delete rad[k];
              return { ...p, [veckoRad]: rad };
            });
            setDirty(true);
          }}
          onApply={(perMan) => {
            const filtrerat = {};
            for (const m of manader) if (perMan[m]) filtrerat[m] = perMan[m];
            setRader((prev) => ({ ...prev, [veckoRad]: filtrerat }));
            setDirty(true);
            setMsg(`Veckobudgeten inskriven på "${raderDef.find((r) => r.id === veckoRad)?.label}" — spara när du är klar`);
          }}
          onClose={() => setVeckoRad(null)}
        />
      )}

      {/* Personal: fast/säsong ur Ledning → Personal */}
      {personalPanel && !last && (
        <PersonalSplitPanel
          ra={ra} bolag={bolag}
          onApply={(perRad) => {
            const skyddade = Object.keys(perRad).filter((id) => radStatus[id] === 'klar');
            const skrivs = Object.keys(perRad).filter((id) => radStatus[id] !== 'klar');
            if (!skrivs.length) { setMsg('Båda personalraderna är markerade klara (●) — inget skrevs.'); return; }
            const namnAv = (id) => raderDef.find((r) => r.id === id)?.label || id;
            if (skyddade.length && !window.confirm(
              `Skriver ${skrivs.map(namnAv).join(' och ')}.\nHoppar över ${skyddade.map(namnAv).join(', ')} (markerade klara).\n\nFortsätt?`)) return;
            setRader((prev) => {
              const nya = { ...prev };
              for (const id of skrivs) nya[id] = perRad[id];
              return nya;
            });
            setDirty(true);
            setPersonalPanel(false);
            setMsg(`Personalraderna inskrivna${skyddade.length ? ` (${skyddade.length} klarmarkerad orörd)` : ''} — justera och spara.`);
          }}
          onClose={() => setPersonalPanel(false)}
        />
      )}

      {/* Budgetassistenten (chatt — alla radtyper) */}
      {assistRad && !last && (
        <AssistentPanel
          ra={ra}
          bolag={bolag}
          radId={assistRad}
          radDef={raderDef.find((r) => r.id === assistRad)}
          raderDef={raderDef}
          rader={rader}
          onApply={(perYm) => {
            setRader((prev) => ({ ...prev, [assistRad]: { ...(prev[assistRad] || {}), ...perYm } }));
            setDirty(true);
            setMsg(`Assistentens förslag inskrivet på "${raderDef.find((r) => r.id === assistRad)?.label}" — spara när du är klar`);
          }}
          onClose={() => setAssistRad(null)}
        />
      )}

      {/* Förutsättningar */}
      <div className="card">
        <div className="card-title">Förutsättningar — antaganden bakom budgeten</div>
        <textarea
          value={forutsattningar} disabled={last}
          onChange={(e) => { setForutsattningar(e.target.value); setDirty(true); }}
          placeholder={'T.ex.\n– Intäkter upp 6,5 %\n– Direkta kostnader upp 3 %\n– Personal: …'}
          style={{ width: '100%', minHeight: 90, border: '1px solid var(--border)', borderRadius: 8, padding: 10, fontSize: 12.5, fontFamily: 'system-ui,sans-serif', background: last ? 'var(--panel)' : 'var(--card)', color: 'var(--text)', resize: 'vertical' }}
        />
      </div>
    </>
  );
}
